<template>
  <div class="movement-processing-view">
    <!-- Vista para procesamiento -->
    <v-card-text v-if="isProcessing && lastMovement && lastMovement.type !== 'entry'" class="pt-0">
      <v-alert v-if="isMovementCompleted" type="success" class="mb-4">
        Este movimiento está completado. Los campos están bloqueados para su edición.
      </v-alert>
      <v-alert v-else type="info" class="mb-4 mt-2 py-3 rounded-xl" icon="mdi-information-outline">
        {{ 
          lastMovement && lastMovement.type === 'assignment'              
            ? (lastMovement && (lastMovement.status === 'unreported' || lastMovement.status === 'reported')
              ? 'Esta cesión está pendiente de entrega. Complete los detalles necesarios y la firma del conductor.' 
              : lastMovement && lastMovement.status === 'processing' 
                ? 'Esta cesión está en proceso. Añade los kilómetros finales o modifique los detalles necesarios.' 
                : 'Esta cesión está en proceso de salida.')
            : (lastMovement && lastMovement.status === 'reported' 
              ? 'Este movimiento está reportado. Complete o modifique los detalles necesarios.' 
              : lastMovement && lastMovement.status === 'processing' 
                ? 'Este movimiento está en proceso. Añade los kilómetros finales o modifique los detalles necesarios.' 
                : lastMovement && lastMovement.type === 'entry' 
                  ? 'Este movimiento está en proceso de creación, rectifique los datos.' 
                  : 'Este movimiento está en proceso de salida.')
        }}
      </v-alert>
      
      <!-- Datos principales del vehículo -->
      <v-card class="mb-4" outlined>
        <v-card-title class="primary white--text py-3">
          <v-icon left color="white">mdi-car</v-icon>
          Datos del vehículo
        </v-card-title>

        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-list-item three-line class="px-0">
                <v-list-item-avatar size="56" color="primary" class="white--text">
                  <v-icon large>mdi-car</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-1 font-weight-bold">
                    {{ status.brand }} {{ status.model }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ status.version || 'Sin versión' }} • {{ status.color || 'Sin color' }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="caption">
                    <v-chip x-small label outlined color="primary" class="mr-1">
                      {{ status.condition === 'new' ? 'Nuevo' : 'Usado' }}
                    </v-chip>
                    <span v-if="status.plate_number">Matrícula: {{ status.plate_number }}</span>
                    <span v-else class="warning--text">Sin matrícula</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <v-col cols="12" md="6">
              <v-list dense class="transparent">
                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="primary">mdi-engine</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="caption grey--text">Motor</v-list-item-title>
                    <v-list-item-subtitle class="subtitle-2">{{ status.engine || 'No especificado' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="primary">mdi-identifier</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="caption grey--text">No. de Bastidor</v-list-item-title>
                    <v-list-item-subtitle class="subtitle-2">{{ status.frame_number || 'No especificado' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="primary">mdi-map-marker</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="caption grey--text">Ubicación inicial</v-list-item-title>
                    <v-list-item-subtitle class="subtitle-2">{{ getLocationName(initialLocation) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Formulario para modificar/completar el movimiento -->
      <v-card outlined>
        <v-card-title class="primary white--text py-3">
          <v-icon left color="white">mdi-truck</v-icon>
          Datos del movimiento
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Sección de transporte -->
          <v-card flat class="mb-4">
            <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-account-question</v-icon>
              Método de transporte
            </v-card-subtitle>

            <v-divider class="mb-4"></v-divider>

            <v-row>
              <v-col cols="12" md="6">
                <v-select 
                  :value="transportMethod" 
                  :items="availableTransportOptions"
                  label="¿QUIÉN REALIZARÁ EL TRANSPORTE?" 
                  outlined 
                  required 
                  prepend-inner-icon="mdi-truck"
                  :rules="validations.transportation" 
                  :error-messages="errors.transportation"
                  :disabled="isMovementCompleted || lastMovement?.status === 'processing'"
                  @input="$emit('update:transportMethod', $event)"
                />
              </v-col>

              <v-col cols="12" md="6" v-if="transportMethod === 'GRUA'">
                <grua-selector
                  :value="selectedGrua"
                  :current-dealer-id="currentDealerId"
                  :last-movement="lastMovement"
                  :is-processing="isProcessing"
                  :disabled="isMovementCompleted"
                  :required="(this.lastMovement?.status === 'reported' || this.lastMovement?.status === 'processing') && !this.selectedGrua"
                  @grua-created="$emit('grua-created', $event)"
                  @input="$emit('update:selectedGrua', $event)"
                />
              </v-col>

              <v-col cols="12" md="6">
                <driver-selector
                   v-if="(transportMethod === 'DRIVER' || transportMethod === 'CLIENTE') || (transportMethod === 'YO MISMO' && lastMovement && lastMovement.status === 'reported')"
                  v-model="localSelectedDriver"
                  :current-dealer-id="currentDealerId"
                  :transport-method="transportMethod"
                  :last-movement="lastMovement"
                  :current-user="currentUser"
                  :first="true"
                  :disabled="isMovementCompleted || lastMovement?.status === 'processing'"
                  :label="isDriverRequiredForAssignment ? '¿QUIÉN ES EL CONDUCTOR? *' : '¿QUIÉN ES EL CONDUCTOR?'"
                  :error="isDriverMissing"
                  :hint="'Para buscar el conductor es necesario añadir el número del DNI'"
                  :persistent-hint="true"
                  @driver-created="$emit('driver-created', $event)"
                  @input="$emit('update:selectedDriver', $event)"
                />
                <v-alert v-if="localSelectedDriver && localSelectedDriver.document_expiration_date && new Date(localSelectedDriver.document_expiration_date) < new Date()" type="warning" class="mt-2" dense text>
                  <div class="d-flex align-center">
                    <div>La licencia de conducción está caducada. Por favor, seleccione otro conductor.</div>
                  </div>
                </v-alert>

                <v-checkbox
                  v-if="lastMovement && (lastMovement.status === 'processing' || lastMovement.status === 'reported') && !isMovementCompleted"
                  v-model="localShowSecondDriver"
                  class="mt-2"
                  label="Añadir nuevo conductor"
                />

                <driver-selector
                  v-if="localShowSecondDriver"
                  v-model="localSecondDriver"
                  :current-dealer-id="currentDealerId"
                  :transport-method="transportMethod"
                  :last-movement="lastMovement"
                  :current-user="currentUser"
                  :first="false"
                  :disabled="isMovementCompleted"
                  label="Nuevo conductor"
                  hint="Para buscar el conductor es necesario añadir el número del DNI"
                  :persistent-hint="true"
                  @driver-created="$emit('driver-created', $event)"
                  @input="$emit('update:selectedSecondDriver', $event)"
                />
              </v-col>
            </v-row>
          </v-card>
          
          <!-- Sección de fecha y hora -->
          <v-card flat class="mb-4">
            <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-calendar-clock</v-icon>
              Fecha y hora
            </v-card-subtitle>

            <v-divider class="mb-4"></v-divider>

            <v-row>
                <v-col cols="12" md="6" v-if="lastMovement && (lastMovement.status === 'processing' || lastMovement.status === 'reported')">
                  <custom-date-time-picker
                    :value="deliveryDate || Math.floor(Date.now() / 1000)"
                    label="Fecha y hora de entrega"
                    :rules="validations.datetime"
                    :isAssignment="(lastMovement && lastMovement.type === 'assignment') ? true : false"
                    :lastMovement="lastMovement"
                    :movements="status.movements"
                    :error-messages="errors.datetime"
                    prepend-inner-icon="mdi-calendar-clock"
                    dense
                    :disabled="isMovementCompleted"
                    @input="$emit('update:delivery-date', convertDatetimeToTimestamp($event))"
                  />
                </v-col>

                <v-col cols="12" md="6" v-else>
                  <custom-date-time-picker
                    :value="datetime || Math.floor(Date.now() / 1000)"
                    label="Fecha y hora del movimiento"
                    :rules="validations.datetime"
                    :isAssignment="(lastMovement && lastMovement.type === 'assignment') ? true : false"
                    :lastMovement="lastMovement"
                    :movements="status.movements"
                    :error-messages="errors.datetime"
                    prepend-inner-icon="mdi-calendar-clock"
                    dense
                    :disabled="isMovementCompleted"
                    @input="$emit('update:datetime', convertDatetimeToTimestamp($event))"
                  />
                </v-col>

              <v-col cols="12" md="6" v-if="(lastMovement && lastMovement.type === 'assignment') || (lastMovement && lastMovement.type === 'move') || (transportMethod === 'CLIENTE' && status.status !== 'created')">
              <custom-date-time-picker
                :value="endDate"
                label="Fecha de devolución"
                :rules="validations.datetime"
                :isAssignment="(lastMovement && lastMovement.type === 'assignment') ? true : false"
                :lastMovement="lastMovement"
                :movements="status.movements"
                :error-messages="errors.datetime"
                prepend-inner-icon="mdi-calendar-clock"
                dense
                :disabled="isMovementCompleted"
                @input="$emit('update:endDate', convertEndDateToTimestamp($event))"
              />
              </v-col>

              <v-col cols="12" md="6" v-if="lastMovement && lastMovement.status === 'processing'">
              <custom-date-time-picker
                :value="receptionDatetime"
                label="Fecha de recepción"
                :rules="validations.datetime"
                :isAssignment="(lastMovement && lastMovement.type === 'assignment') ? true : false"
                :lastMovement="lastMovement"
                :movements="status.movements"
                :error-messages="errors.datetime"
                prepend-inner-icon="mdi-calendar-clock"
                dense
                :disabled="isMovementCompleted"
                @input="$emit('update:receptionDatetime', convertReceptionDatetimeToTimestamp($event))"
              />
              </v-col>
            </v-row>
          </v-card>
          
          <!-- Sección de ubicaciones -->
          <v-card flat class="mb-4">
            <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-map-marker-path</v-icon>
              Origen y destino
            </v-card-subtitle>

            <v-divider class="mb-4"></v-divider>

            <v-row>
              <v-col cols="12" md="6">
                <v-select 
                  :value="initialLocation" 
                  :items="resolvedLocationOptions" 
                  item-text="displayName" 
                  item-value="id"
                  label="Ubicación inicial" 
                  outlined 
                  required 
                  prepend-inner-icon="mdi-map-marker"
                  :rules="validations.source_location_id" 
                  :error-messages="errors.source_location_id"
                  disabled
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select 
                  :value="finalLocation" 
                  :items="resolvedLocationOptions" 
                  item-text="displayName" 
                  item-value="id"
                  label="Ubicación final" 
                  outlined 
                  required 
                  prepend-inner-icon="mdi-map-marker-check"
                  :rules="validations.destination_location_id" 
                  :error-messages="errors.destination_location_id"
                  :disabled="isMovementCompleted"
                  @input="$emit('update:finalLocation', $event)"
                />
              </v-col>
            </v-row>
          </v-card>

          <!-- Sección de datos del vehículo -->
          <v-card flat class="mb-4">
            <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-car-cog</v-icon>
              Información del vehículo
            </v-card-subtitle>

            <v-divider class="mb-4"></v-divider>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field 
                  :value="initialKm" 
                  label="Kilómetros iniciales" 
                  outlined 
                  required
                  prepend-inner-icon="mdi-speedometer" 
                  :rules="validations.initial_km"
                  :error-messages="initialKmErrorMessage || errors.initial_km" 
                  :error="!initialKm || initialKm === '' || isInitialKmInvalid"
                  :color="isInitialKmInvalid ? 'error' : 'primary'"
                  :disabled="isMovementCompleted || lastMovement.status === 'processing'"
                  :hint="status && status.current_kms ? `KMs del último movimiento: ${status.current_kms}` : ''"
                  persistent-hint
                  @input="$emit('update:initialKm', $event)"
                />
                <v-alert
                  v-if="isInitialKmInvalid && initialKm && status && status.current_kms"
                  type="error"
                  dense
                  text
                  class="mt-2"
                >
                  <v-icon left small>mdi-alert</v-icon>
                  Los kilómetros iniciales han de ser mayor a {{ status.current_kms }} km (kilómetros de la recepción del movimiento anterior)
                </v-alert>
              </v-col>
              <v-col cols="12" md="6" v-if="lastMovement && lastMovement.status == 'processing'">
                <v-text-field 
                  :value="finalKm" 
                  label="Kilómetros finales" 
                  outlined 
                  required
                  prepend-inner-icon="mdi-speedometer" 
                  :rules="validations.final_km"
                  :error-messages="finalKmErrorMessage || errors.final_km" 
                  :error="!finalKm || finalKm === '' || isFinalKmInvalid"
                  :color="isFinalKmInvalid ? 'error' : 'primary'"
                  :disabled="isMovementCompleted"
                  hint="Deben ser mayores que los kilómetros iniciales"
                  persistent-hint
                  @input="$emit('update:finalKm', $event)"
                />
                <v-alert
                  v-if="isFinalKmInvalid && finalKm && initialKm"
                  type="error"
                  dense
                  text
                  class="mt-2"
                >
                  <v-icon left small>mdi-alert</v-icon>
                  Los kilómetros finales ({{ finalKm }}) deben ser mayores que los iniciales ({{ initialKm }})
                </v-alert>
              </v-col>

              <v-col cols="12" md="6">
                <v-select 
                  :value="driverType" 
                  :items="vehicleTypeOptions" 
                  item-text="text" 
                  item-value="value"
                  label="Estado de matrícula" 
                  outlined 
                  required 
                  prepend-inner-icon="mdi-tag-outline"
                  :rules="validations.registered" 
                  :error-messages="errors.registered"
                  :error="!driverType || driverType === ''"
                  :disabled="isMovementCompleted"
                  hint="Campo obligatorio"
                  persistent-hint
                  @input="$emit('update:driverType', $event)"
                />
              </v-col>
            </v-row>

            <!-- Card para mostrar información adicional de creación -->
            <v-card 
              v-if="lastMovement.additional_info" 
              class="mb-4 pa-4 info-card-blue" 
              elevation="3"
            >
              <div class="d-flex align-center mb-2">
                <v-icon color="blue darken-2" class="mr-2">mdi-information</v-icon>
                <span class="subtitle-2 font-weight-bold blue--text text--darken-2">Creación petición</span>
              </div>
              <div class="body-2" style="white-space: pre-wrap;">{{ lastMovement.additional_info }}</div>
            </v-card>

            <!-- Card para mostrar información adicional de entrega -->
            <v-card 
              v-if="lastMovement.additional_info_delivery" 
              class="mb-4 pa-4 info-card-green" 
              elevation="3"
            >
              <div class="d-flex align-center mb-2">
                <v-icon color="green darken-2" class="mr-2">mdi-information</v-icon>
                <span class="subtitle-2 font-weight-bold green--text text--darken-2">Entrega movimiento</span>
              </div>
              <div class="body-2" style="white-space: pre-wrap;">{{ lastMovement.additional_info_delivery }}</div>
            </v-card>

            <!-- Sección de Franquicia y Límite de KM - Solo para cesiones (reportadas o processing) -->
            <v-card flat class="mb-4" v-if="lastMovement && lastMovement.type === 'assignment' && (lastMovement.status === 'reported' || lastMovement.status === 'processing')">
              <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
                <v-icon left color="primary">mdi-cog</v-icon>
                Configuración de cesión
              </v-card-subtitle>

              <v-divider class="mb-4"></v-divider>

              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    :value="franchise"
                    :items="franchiseOptions"
                    item-text="text"
                    item-value="value"
                    label="Franquicia"
                    outlined
                    dense
                    prepend-inner-icon="mdi-crown"
                    :disabled="isMovementCompleted || (lastMovement && lastMovement.status === 'processing')"
                    :hint="lastMovement && lastMovement.status === 'processing' ? 'Configuración establecida en la entrega' : 'Seleccione la franquicia a aplicar'"
                    persistent-hint
                    @input="$emit('update:franchise', $event)"
                  >
                    <template v-slot:selection="{ item }">
                      <v-icon v-if="item.isDefault" left small color="primary">mdi-star</v-icon>
                      {{ item.text }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-icon v-if="item.isDefault" left small color="primary">mdi-star</v-icon>
                      {{ item.text }}
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    :value="kmLimit"
                    :items="kmLimitOptions"
                    item-text="text"
                    item-value="value"
                    label="Límite de KM diarios"
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker-distance"
                    :disabled="isMovementCompleted || (lastMovement && lastMovement.status === 'processing')"
                    :hint="lastMovement && lastMovement.status === 'processing' ? 'Configuración establecida en la entrega' : 'Seleccione el límite de kilómetros'"
                    persistent-hint
                    @input="$emit('update:kmLimit', $event)"
                  >
                    <template v-slot:selection="{ item }">
                      <v-icon v-if="item.isDefault" left small color="success">mdi-star</v-icon>
                      {{ item.text }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-icon v-if="item.isDefault" left small color="success">mdi-star</v-icon>
                      {{ item.text }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-card>

            <!-- Modal previsualización comodato -->
            <v-dialog v-model="showComodatoPreview" max-width="900" scrollable>
              <v-card>
              <v-card-title class="primary white--text py-3">
                <v-icon left color="white">mdi-file-document-outline</v-icon>
                Contrato de cesión de uso de vehículo de cortesía
                <v-spacer></v-spacer>
                <v-btn icon dark @click="showComodatoPreview = false">
                <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pa-6 d-flex justify-center">
                <div v-html="comodatoHtml"
                style="display:inline-block; max-width:820px; font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #000; text-align:left;"></div>
              </v-card-text>
              <v-card-actions class="px-4 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="showComodatoPreview = false">Cerrar</v-btn>
              </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Sección de información adicional -->
            <div class="text-center">
              <v-checkbox
              v-model="localShowAdditionalInfo"
              class="mt-4 mb-0"
              :disabled="isMovementCompleted"
              >
              <template v-slot:label>
                <div class="d-flex align-center justify-center">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span class="subtitle-1 font-weight-bold">Información adicional</span>
                </div>
              </template>
              </v-checkbox>
            </div>

            <!-- Contenido de información adicional (condicional) -->
            <v-expand-transition>
              <div v-if="localShowAdditionalInfo" class="mt-2 mb-4">
              <!-- Campo de texto para información adicional (dinámico según status) -->
              <v-row class="mb-4">
                <v-col cols="12">
                <v-textarea
                  :value="currentAdditionalInfo"
                  :label="additionalInfoLabel"
                  outlined
                  rows="5"
                  auto-grow
                  :hint="additionalInfoHint"
                  persistent-hint
                  :disabled="isMovementCompleted"
                  @input="handleAdditionalInfoInput"
                />
              </v-col>
            </v-row>

              <!-- Sección de imágenes adicionales -->
              <v-divider class="mb-4"></v-divider>

              <!-- Usar el componente ImageManager para imágenes adicionales -->
              <image-manager
                vehicle-condition="additional"
                :damage-notes="''"
                :saved-images="formattedSavedAdditionalImages"
                :new-damage-images="newAdditionalImages"
                :new-image-previews="newAdditionalImagePreviews"
                :images-to-remove="additionalImagesToRemove"
                @update:damageNotes="() => {}"
                @update:newDamageImages="$emit('update:newAdditionalImages', $event)"
                @update:newImagePreviews="$emit('update:newAdditionalImagePreviews', $event)"
                @update:imagesToRemove="$emit('update:additionalImagesToRemove', $event)"
                @images-changed="$emit('additional-images-changed', $event)"
                @image-removed="$emit('additional-image-removed', $event)"
              />

              <!-- Sección de documentos adicionales -->
              <v-divider class="my-4"></v-divider>

              <div class="subtitle-2 mb-3">
                <v-icon left color="primary">mdi-file-document-multiple-outline</v-icon>
                Documentos adicionales
              </div>

              <!-- Documentos guardados -->
              <div v-if="formattedSavedAdditionalDocs && formattedSavedAdditionalDocs.length > 0" class="mb-4">
                <div class="subtitle-2 mb-2">
                <v-icon small left color="primary">mdi-file-check</v-icon>
                Documentos guardados ({{ formattedSavedAdditionalDocs.length - additionalDocsToRemove.length }})
                </div>
                <v-list dense>
                <v-list-item
                  v-for="(doc, index) in formattedSavedAdditionalDocs"
                  :key="`saved-doc-${index}`"
                  :class="{ 'disabled-item': additionalDocsToRemove.includes(doc.id) }"
                >
                  <v-list-item-avatar>
                  <v-icon :color="getFileIconColor(doc.filename || doc.name)">{{ getFileIcon(doc.filename || doc.name) }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                  <v-list-item-title>{{ doc.filename || doc.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="doc.size">{{ (doc.size / 1024).toFixed(2) }} KB</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                  <div class="d-flex">
                    <v-btn
                    v-if="!additionalDocsToRemove.includes(doc.id) && !isMovementCompleted"
                    icon
                    small
                    @click="downloadDoc(doc.url, doc.filename || doc.name)"
                    >
                    <v-icon small>mdi-download</v-icon>
                    </v-btn>
                    <v-btn
                    v-if="!additionalDocsToRemove.includes(doc.id) && !isMovementCompleted"
                    icon
                    small
                    color="error"
                    @click="removeDoc(index)"
                    >
                    <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                    <v-chip
                    v-if="additionalDocsToRemove.includes(doc.id)"
                    x-small
                    color="error"
                    >
                    Eliminado
                    </v-chip>
                  </div>
                  </v-list-item-action>
                </v-list-item>
                </v-list>
              </div>

              <!-- Nuevos documentos -->
              <div v-if="newAdditionalDocs && newAdditionalDocs.length > 0" class="mb-4">
                <div class="subtitle-2 mb-2">
                <v-icon small left color="success">mdi-file-plus</v-icon>
                Nuevos documentos ({{ newAdditionalDocs.length }})
                </div>
                <v-list dense>
                <v-list-item
                  v-for="(doc, index) in newAdditionalDocs"
                  :key="`new-doc-${index}`"
                  class="new-doc-item"
                >
                  <v-list-item-avatar>
                  <v-icon :color="getFileIconColor(doc.name)">{{ getFileIcon(doc.name) }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                  <v-list-item-title>{{ doc.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ (doc.size / 1024).toFixed(2) }} KB</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="removeNewDoc(index)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                  </v-list-item-action>
                  <v-chip class="ml-2" x-small color="success">
                  Nuevo
                  </v-chip>
                </v-list-item>
                </v-list>
              </div>

              <!-- Input para subir documentos -->
              <v-file-input
                v-if="!isMovementCompleted && canAddMoreDocs"
                v-model="tempDocs"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                multiple
                label="Agregar documentos adicionales"
                prepend-icon="mdi-paperclip"
                outlined
                dense
                chips
                show-size
                @change="handleDocUpload"
                :hint="`Puedes agregar ${remainingDocSlots} documento(s) más. Máximo 4 documentos. Formatos: PDF, DOC, DOCX, XLS, XLSX, TXT, ZIP, RAR`"
                persistent-hint
              ></v-file-input>

              <v-alert
                v-if="!canAddMoreDocs && !isMovementCompleted"
                type="info"
                dense
                class="mt-2"
              >
                <v-icon left small>mdi-information</v-icon>
                Has alcanzado el límite máximo de 4 documentos.
              </v-alert>
              </div>
            </v-expand-transition>

            <!-- Estado del vehículo (daños) -->
            <v-card-subtitle class="px-0 pt-4 pb-2 subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-car-info</v-icon>
              Estado del vehículo
            </v-card-subtitle>

            <v-radio-group 
              :value="vehicleCondition" 
              required 
              :rules="validations.vehicle_status"
              :error-messages="errors.vehicle_status" 
              class="vehicle-condition-group mb-4"
              :disabled="isMovementCompleted"
              @change="$emit('update:vehicleCondition', $event)"
            >
              <v-row>
              <v-col cols="6">
                <v-hover v-slot="{ hover }">
                <v-card 
                  :elevation="(!isMovementCompleted && (hover || vehicleCondition === 'good')) ? 6 : 1"
                  class="pa-4 text-center transition-swing"
                  :class="{ 'success lighten-5': vehicleCondition === 'good', 'disabled-card': isMovementCompleted }"
                  @click="!isMovementCompleted && $emit('update:vehicleCondition', 'good')"
                >
                  <v-icon size="48" color="success">mdi-check-circle</v-icon>
                  <div class="subtitle-1 mt-3 font-weight-medium">BUENO</div>
                  <v-radio value="good" class="vehicle-condition-radio" hide-details></v-radio>
                    </v-card>
                  </v-hover>
                </v-col>
                <v-col cols="6">
                  <v-hover v-slot="{ hover }">
                    <v-card 
                      :elevation="(!isMovementCompleted && (hover || vehicleCondition === 'damages')) ? 6 : 1"
                      class="pa-4 text-center transition-swing"
                      :class="{ 'error lighten-5': vehicleCondition === 'damages', 'disabled-card': isMovementCompleted }"
                      @click="!isMovementCompleted && $emit('update:vehicleCondition', 'damages')"
                    >
                      <v-icon size="48" color="error">mdi-alert-circle</v-icon>
                      <div class="subtitle-1 mt-3 font-weight-medium">HAY DAÑOS</div>
                      <v-radio value="damages" class="vehicle-condition-radio" hide-details></v-radio>
                    </v-card>
                  </v-hover>
                </v-col>
              </v-row>
            </v-radio-group>

            <!-- Sección de daños (condicional) -->
            <image-manager
              :vehicle-condition="vehicleCondition || ''"
              :damage-notes="damageNotes"
              :delivery-observations="lastMovement ? lastMovement.observations : ''"
              :saved-images="formattedSavedImages"
              :new-damage-images="newDamageImages"
              :new-image-previews="newImagePreviews"
              :images-to-remove="imagesToRemove"
              @update:damageNotes="$emit('update:damageNotes', $event)"
              @update:newDamageImages="$emit('update:newDamageImages', $event)"
              @update:newImagePreviews="$emit('update:newImagePreviews', $event)"
              @update:imagesToRemove="$emit('update:imagesToRemove', $event)"
              @images-changed="$emit('images-changed', $event)"
              @image-removed="$emit('image-removed', $event)"
            />

            <!-- Sección de documentos de daños (condicional) -->
            <div v-if="vehicleCondition === 'damages'">
              <v-divider class="my-4"></v-divider>
              
              <div class="subtitle-2 mb-3">
                <v-icon left color="error">mdi-file-document-alert-outline</v-icon>
                Documentos de daños
              </div>

              <!-- Documentos de daños guardados -->
              <div v-if="formattedSavedDamageDocs && formattedSavedDamageDocs.length > 0" class="mb-4">
                <div class="subtitle-2 mb-2">
                  <v-icon small left color="primary">mdi-file-check</v-icon>
                  Documentos guardados ({{ formattedSavedDamageDocs.length - damageDocsToRemove.length }})
                </div>
                <v-list dense>
                  <v-list-item
                    v-for="(doc, index) in formattedSavedDamageDocs"
                    :key="`saved-damage-doc-${index}`"
                    :class="{ 'disabled-item': damageDocsToRemove.includes(doc.id) }"
                  >
                    <v-list-item-avatar>
                      <v-icon :color="getFileIconColor(doc.filename || doc.name)">{{ getFileIcon(doc.filename || doc.name) }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ doc.filename || doc.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="doc.size">{{ (doc.size / 1024).toFixed(2) }} KB</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <div class="d-flex">
                        <v-btn
                          v-if="!damageDocsToRemove.includes(doc.id) && !isMovementCompleted"
                          icon
                          small
                          @click="downloadDoc(doc.url, doc.filename || doc.name)"
                        >
                          <v-icon small>mdi-download</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="!damageDocsToRemove.includes(doc.id) && !isMovementCompleted"
                          icon
                          small
                          color="error"
                          @click="removeDamageDoc(index)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                        <v-chip
                          v-if="damageDocsToRemove.includes(doc.id)"
                          x-small
                          color="error"
                        >
                          Eliminado
                        </v-chip>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>

              <!-- Nuevos documentos de daños -->
              <div v-if="newDamageDocs && newDamageDocs.length > 0" class="mb-4">
                <div class="subtitle-2 mb-2">
                  <v-icon small left color="success">mdi-file-plus</v-icon>
                  Nuevos documentos ({{ newDamageDocs.length }})
                </div>
                <v-list dense>
                  <v-list-item
                    v-for="(doc, index) in newDamageDocs"
                    :key="`new-damage-doc-${index}`"
                    class="new-doc-item"
                  >
                    <v-list-item-avatar>
                      <v-icon :color="getFileIconColor(doc.name)">{{ getFileIcon(doc.name) }}</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ doc.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ (doc.size / 1024).toFixed(2) }} KB</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeNewDamageDoc(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                    <v-chip class="ml-2" x-small color="success">
                      Nuevo
                    </v-chip>
                  </v-list-item>
                </v-list>
              </div>

              <!-- Input para subir documentos de daños -->
              <v-file-input
                v-if="!isMovementCompleted && canAddMoreDamageDocs"
                v-model="tempDamageDocs"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                multiple
                label="Agregar documentos de daños"
                prepend-icon="mdi-paperclip"
                outlined
                dense
                chips
                show-size
                @change="handleDamageDocUpload"
                :hint="`Puedes agregar ${remainingDamageDocSlots} documento(s) más. Máximo 4 documentos. Formatos: PDF, DOC, DOCX, XLS, XLSX, TXT, ZIP, RAR`"
                persistent-hint
              ></v-file-input>

              <v-alert
                v-if="!canAddMoreDamageDocs && !isMovementCompleted"
                type="info"
                dense
                class="mt-2"
              >
                <v-icon left small>mdi-information</v-icon>
                Has alcanzado el límite máximo de 4 documentos de daños.
              </v-alert>
            </div>
          </v-card>

          <!-- Sección de equipamiento 
          <v-card flat class="mb-4">-->
            <!-- <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
              <v-icon left color="deep-orange">mdi-car-emergency</v-icon>
              Equipo básico de emergencia
            </v-card-subtitle>

            <v-divider class="mb-4"></v-divider>

            <v-row>
              <v-col cols="12" md="4">
                <v-hover v-slot="{ hover }">
                  <v-card 
                    :elevation="(!isMovementCompleted && hover) ? 4 : 1"
                    class="pa-4 text-center transition-swing"
                    :class="{ 'primary lighten-4': emergencyLight, 'disabled-card': isMovementCompleted }"
                    @click="!isMovementCompleted && $emit('update:emergencyLight', !emergencyLight)"
                  >
                    <v-icon size="48" :color="emergencyLight ? 'primary' : 'grey'">mdi-car-light-high</v-icon>
                    <div class="subtitle-2 mt-2">Luces de emergencia</div>
                  </v-card>
                </v-hover>
              </v-col>

              <v-col cols="12" md="4">
                <v-hover v-slot="{ hover }">
                  <v-card 
                    :elevation="(!isMovementCompleted && hover) ? 4 : 1"
                    class="pa-4 text-center transition-swing"
                    :class="{ 'amber lighten-4': emergencyTriangle, 'disabled-card': isMovementCompleted }"
                    @click="!isMovementCompleted && $emit('update:emergencyTriangle', !emergencyTriangle)"
                  >
                    <v-icon size="48" :color="emergencyTriangle ? 'amber darken-2' : 'grey'">mdi-triangle-outline</v-icon>
                    <div class="subtitle-2 mt-2">Triángulo de emergencia</div>
                  </v-card>
                </v-hover>
              </v-col>

              <v-col cols="12" md="4">
                <v-hover v-slot="{ hover }">
                  <v-card 
                    :elevation="(!isMovementCompleted && hover) ? 4 : 1"
                    class="pa-4 text-center transition-swing"
                    :class="{ 'orange lighten-4': emergencyVest, 'disabled-card': isMovementCompleted }"
                    @click="!isMovementCompleted && $emit('update:emergencyVest', !emergencyVest)"
                  >
                    <v-icon size="48" :color="emergencyVest ? 'orange' : 'grey'">mdi-tshirt-crew</v-icon>
                    <div class="subtitle-2 mt-2">Chaleco reflectante</div>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
          </v-card> -->
          <equipment-step-block
            :vehicle="status"
            :emergency-light="!!emergencyLight"
            :emergency-triangle="!!emergencyTriangle"
            :emergency-vest="!!emergencyVest"
            :documentation="!!documentation"
            :charging-cable="!!chargingCable"
            :selected-equipments-from-parent="selectedEquipments"
            :loading="loading"
            @update:emergencyLight="$emit('update:emergencyLight', $event)"
            @update:emergencyTriangle="$emit('update:emergencyTriangle', $event)"
            @update:emergencyVest="$emit('update:emergencyVest', $event)"
            @update:documentation="$emit('update:documentation', $event)"
            @update:chargingCable="$emit('update:chargingCable', $event)"
            @update:selectedEquipments="handleSelectedEquipmentsUpdate"
          />

          <v-checkbox
            v-if="lastMovement && lastMovement.type === 'assignment' && localSelectedDriver"
            v-model="localAcceptTreatment"
            class="mt-4 mb-0 ml-4"
            :disabled="isMovementCompleted || (lastMovement && lastMovement.status === 'processing')"
          >
            <template v-slot:label>
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span class="subtitle-1 font-weight-bold">{{ localSelectedDriver.name }} {{ localSelectedDriver.lastname }} acepta el tratamiento de sus datos personales conforme al RGPD</span>
              </div>
            </template>
          </v-checkbox>

          <v-checkbox
            v-if="lastMovement && lastMovement.type === 'assignment' && localShowSecondDriver && localSecondDriver"
            v-model="localAcceptTreatmentDriver2"
            class="mt-4 mb-0 ml-4"
            :disabled="isMovementCompleted"
          >
            <template v-slot:label>
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span class="subtitle-1 font-weight-bold">{{ localSecondDriver.name }} {{ localSecondDriver.lastname }} acepta el tratamiento de sus datos personales conforme al RGPD</span>
              </div>
            </template>
          </v-checkbox>
          <div class="text-center mb-8">
            <v-btn v-if="lastMovement && lastMovement.type === 'assignment'" outlined color="primary" class="mt-3 mb-0"
              @click="showComodatoPreview = true">
              <v-icon left>mdi-file-document-eye-outline</v-icon>
              Previsualizar comodato
            </v-btn>
          </div>

          <!-- Customer signature section for delivery (entrega) -->
          <v-card v-if="shouldShowCustomerSignature" class="mb-4 equipment-card" outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="green">mdi-draw</v-icon>
              Firma de {{ localSelectedDriver ? localSelectedDriver.name + ' ' + localSelectedDriver.lastname : 'Conductor' }}
              <v-chip v-if="lastMovement.type === 'assignment'" small color="error" class="ml-2">Obligatorio</v-chip>
            </v-card-title>
            <v-card-text>
              <v-alert 
                v-if="lastMovement.type === 'assignment' && !customerSignatureDataURL" 
                type="warning" 
                dense 
                text 
                class="mb-3"
              >
                <v-icon left small>mdi-alert</v-icon>
                La firma del conductor es obligatoria para completar la entrega de una cesión
              </v-alert>
              <div class="text-center">
                <div v-if="!showCustomerSignature" class="py-6">
                  <v-btn 
                    color="green" 
                    outlined 
                    @click="initCustomerSignaturePad"
                  >
                    <v-icon left>mdi-draw</v-icon>
                    Agregar Firma de Entrega
                  </v-btn>
                </div>
                
                <div v-if="showCustomerSignature" class="signature-container">
                  <div class="text-subtitle-2 mb-2">Cliente: Firme para confirmar la entrega</div>
                  <div class="signature-wrapper">
                    <canvas 
                      ref="customerSignatureCanvas" 
                      class="signature-canvas"
                      :style="{
                        border: '2px solid #4CAF50',
                        borderRadius: '4px',
                        backgroundColor: 'white'
                      }"
                    />
                  </div>
                  <div class="mt-3">
                    <v-btn 
                      small 
                      text 
                      color="grey" 
                      @click="clearCustomerSignature"
                      class="mr-2"
                    >
                      <v-icon left small>mdi-eraser</v-icon>
                      Limpiar
                    </v-btn>
                    <v-btn 
                      small 
                      color="green" 
                      @click="saveCustomerSignature"
                      :disabled="!hasCustomerSignatureData"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Guardar
                    </v-btn>
                  </div>
                </div>

                <div v-if="customerSignatureDataURL && !showCustomerSignature" class="signature-preview">
                  <div class="text-subtitle-2 mb-2">Firma de entrega guardada</div>
                  <img :src="customerSignatureDataURL" class="signature-image" alt="Firma del cliente para entrega">
                  <div class="mt-2">
                    <v-btn 
                      small 
                      text 
                      color="green" 
                      @click="editCustomerSignature"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Editar Firma
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Customer signature section for return (devolución) -->
          <v-card v-if="shouldShowCustomerSignatureReturn" class="mb-4 equipment-card" outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="orange">mdi-draw</v-icon>
              Firma de {{ localSelectedDriver ? localSelectedDriver.name + ' ' + localSelectedDriver.lastname : 'Conductor' }}
              <v-chip v-if="lastMovement.type === 'assignment'" small color="error" class="ml-2">Obligatorio</v-chip>
            </v-card-title>
            <v-card-text>
              <v-alert 
                v-if="lastMovement.type === 'assignment' && !customerSignatureReturnDataURL" 
                type="warning" 
                dense 
                text 
                class="mb-3"
              >
                <v-icon left small>mdi-alert</v-icon>
                La firma del conductor es obligatoria para completar la recepción de una cesión
              </v-alert>
              <div class="text-center">
                <div v-if="!showCustomerSignatureReturn" class="py-6">
                  <v-btn 
                    color="orange" 
                    outlined 
                    @click="initCustomerSignatureReturnPad"
                  >
                    <v-icon left>mdi-draw</v-icon>
                    Agregar Firma de Devolución
                  </v-btn>
                </div>
                
                <div v-if="showCustomerSignatureReturn" class="signature-container">
                  <div class="text-subtitle-2 mb-2">Cliente: Firme para confirmar la devolución</div>
                  <div class="signature-wrapper">
                    <canvas 
                      ref="customerSignatureReturnCanvas" 
                      class="signature-canvas"
                      :style="{
                        border: '2px solid #FF9800',
                        borderRadius: '4px',
                        backgroundColor: 'white'
                      }"
                    />
                  </div>
                  <div class="mt-3">
                    <v-btn 
                      small 
                      text 
                      color="grey" 
                      @click="clearCustomerSignatureReturn"
                      class="mr-2"
                    >
                      <v-icon left small>mdi-eraser</v-icon>
                      Limpiar
                    </v-btn>
                    <v-btn 
                      small 
                      color="orange" 
                      @click="saveCustomerSignatureReturn"
                      :disabled="!hasCustomerSignatureReturnData"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Guardar
                    </v-btn>
                  </div>
                </div>

                <div v-if="customerSignatureReturnDataURL && !showCustomerSignatureReturn" class="signature-preview">
                  <div class="text-subtitle-2 mb-2">Firma de devolución guardada</div>
                  <img :src="customerSignatureReturnDataURL" class="signature-image" alt="Firma del cliente para devolución">
                  <div class="mt-2">
                    <v-btn 
                      small 
                      text 
                      color="orange" 
                      @click="editCustomerSignatureReturn"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Editar Firma
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Customer signature section for driver 2 (entrega) -->
          <v-card v-if="shouldShowDriver2Signature" class="mb-4 equipment-card" outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="green">mdi-draw</v-icon>
              Firma de {{ localSecondDriver ? localSecondDriver.name + ' ' + localSecondDriver.lastname : 'Conductor 2' }}
              <v-chip v-if="lastMovement.type === 'assignment'" small color="error" class="ml-2">Obligatorio</v-chip>
            </v-card-title>
            <v-card-text>
              <v-alert 
                v-if="lastMovement.type === 'assignment' && !driver2SignatureDataURL" 
                type="warning" 
                dense 
                text 
                class="mb-3"
              >
                <v-icon left small>mdi-alert</v-icon>
                La firma del segundo conductor es obligatoria para completar la entrega
              </v-alert>
              <div class="text-center">
                <div v-if="!showDriver2Signature" class="py-6">
                  <v-btn 
                    color="green" 
                    outlined 
                    @click="initDriver2SignaturePad"
                  >
                    <v-icon left>mdi-draw</v-icon>
                    Agregar Firma de {{ localSecondDriver ? localSecondDriver.name : 'Conductor 2' }}
                  </v-btn>
                </div>
                
                <div v-if="showDriver2Signature" class="signature-container">
                  <div class="text-subtitle-2 mb-2">{{ localSecondDriver ? localSecondDriver.name + ' ' + localSecondDriver.lastname : 'Conductor 2' }}: Firme para confirmar la entrega</div>
                  <div class="signature-wrapper">
                    <canvas 
                      ref="driver2SignatureCanvas" 
                      class="signature-canvas"
                      :style="{
                        border: '2px solid #4CAF50',
                        borderRadius: '4px',
                        backgroundColor: 'white'
                      }"
                    />
                  </div>
                  <div class="mt-3">
                    <v-btn 
                      small 
                      text 
                      color="grey" 
                      @click="clearDriver2Signature"
                      class="mr-2"
                    >
                      <v-icon left small>mdi-eraser</v-icon>
                      Limpiar
                    </v-btn>
                    <v-btn 
                      small 
                      color="green" 
                      @click="saveDriver2Signature"
                      :disabled="!hasDriver2SignatureData"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Guardar
                    </v-btn>
                  </div>
                </div>

                <div v-if="driver2SignatureDataURL && !showDriver2Signature" class="signature-preview">
                  <div class="text-subtitle-2 mb-2">Firma de {{ localSecondDriver ? localSecondDriver.name : 'Conductor 2' }} guardada</div>
                  <img :src="driver2SignatureDataURL" class="signature-image" :alt="'Firma de ' + (localSecondDriver ? localSecondDriver.name : 'Conductor 2')">
                  <div class="mt-2">
                    <v-btn 
                      small 
                      text 
                      color="green" 
                      @click="editDriver2Signature"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Editar Firma
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Customer signature section for driver 2 (devolución) -->
          <v-card v-if="shouldShowDriver2SignatureReturn" class="mb-4 equipment-card" outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="orange">mdi-draw</v-icon>
              Firma de {{ localSecondDriver ? localSecondDriver.name + ' ' + localSecondDriver.lastname : 'Conductor 2' }}
              <v-chip v-if="lastMovement.type === 'assignment'" small color="error" class="ml-2">Obligatorio</v-chip>
            </v-card-title>
            <v-card-text>
              <v-alert 
                v-if="lastMovement.type === 'assignment' && !driver2SignatureReturnDataURL" 
                type="warning" 
                dense 
                text 
                class="mb-3"
              >
                <v-icon left small>mdi-alert</v-icon>
                La firma del segundo conductor es obligatoria para completar la recepción
              </v-alert>
              <div class="text-center">
                <div v-if="!showDriver2SignatureReturn" class="py-6">
                  <v-btn 
                    color="orange" 
                    outlined 
                    @click="initDriver2SignatureReturnPad"
                  >
                    <v-icon left>mdi-draw</v-icon>
                    Agregar Firma de Devolución de {{ localSecondDriver ? localSecondDriver.name : 'Conductor 2' }}
                  </v-btn>
                </div>
                
                <div v-if="showDriver2SignatureReturn" class="signature-container">
                  <div class="text-subtitle-2 mb-2">{{ localSecondDriver ? localSecondDriver.name + ' ' + localSecondDriver.lastname : 'Conductor 2' }}: Firme para confirmar la devolución</div>
                  <div class="signature-wrapper">
                    <canvas 
                      ref="driver2SignatureReturnCanvas" 
                      class="signature-canvas"
                      :style="{
                        border: '2px solid #FF9800',
                        borderRadius: '4px',
                        backgroundColor: 'white'
                      }"
                    />
                  </div>
                  <div class="mt-3">
                    <v-btn 
                      small 
                      text 
                      color="grey" 
                      @click="clearDriver2SignatureReturn"
                      class="mr-2"
                    >
                      <v-icon left small>mdi-eraser</v-icon>
                      Limpiar
                    </v-btn>
                    <v-btn 
                      small 
                      color="orange" 
                      @click="saveDriver2SignatureReturn"
                      :disabled="!hasDriver2SignatureReturnData"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Guardar
                    </v-btn>
                  </div>
                </div>

                <div v-if="driver2SignatureReturnDataURL && !showDriver2SignatureReturn" class="signature-preview">
                  <div class="text-subtitle-2 mb-2">Firma de devolución de {{ localSecondDriver ? localSecondDriver.name : 'Conductor 2' }} guardada</div>
                  <img :src="driver2SignatureReturnDataURL" class="signature-image" :alt="'Firma de devolución de ' + (localSecondDriver ? localSecondDriver.name : 'Conductor 2')">
                  <div class="mt-2">
                    <v-btn 
                      small 
                      text 
                      color="orange" 
                      @click="editDriver2SignatureReturn"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Editar Firma
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Asignación opcional de TO-DO en la recepción -->
          <todo-assignment-box
            v-if="lastMovement && lastMovement.status === 'processing'"
            :value="selectedTodos"
            :disabled="isMovementCompleted"
            class="mt-4"
            @input="$emit('update:selectedTodos', $event)"
          />
        </v-card-text>
      </v-card>
    </v-card-text>

    <!-- Botones de acción -->
    <v-card-actions v-if="isProcessing" class="px-4 pb-4">
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('close')" color="grey darken-1">
        Cancelar
      </v-btn>
      <v-btn 
        color="success" 
        @click="handleCompleteMovement" 
        :loading="loading" 
        :disabled="isMovementCompleted || !canComplete"
      >
        <v-icon left>mdi-check</v-icon>
        {{ isMovementCompleted ? 'Completado' : 'Completar' }}
      </v-btn>
    </v-card-actions>

    <!-- Dialog de alerta para movimientos incompletos -->
    <v-dialog v-model="showIncompleteMovementsAlert" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Movimientos Pendientes
        </v-card-title>
        <v-card-text class="pt-4">
          <p>No puedes recepcionar este vehículo porque hay movimientos programados para fechas anteriores a la fecha de recepción.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showIncompleteMovementsAlert = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CustomDateTimePicker from './CustomDateTimePicker.vue'
import DriverSelector from './DriverSelector.vue'
import DriverService from '../services/DriverService'
import LocationService from '../services/LocationService'
import GruaSelector from './GruaSelector.vue'
import ImageManager from './ImageManager.vue'
import SignaturePad from 'signature_pad'
import ComodatoClauseService from '../services/ComodatoClauseService'
import EquipmentStepBlock from './EquipmentStepBlock.vue'
import TodoAssignmentBox from './TodoAssignmentBox.vue'
import moment from 'moment-timezone'

export default {
  name: 'MovementProcessingView',
  components: {
  CustomDateTimePicker,
    DriverSelector,
    GruaSelector,
    ImageManager,
    EquipmentStepBlock,
    TodoAssignmentBox
  },
  props: {
    isProcessing: {
      type: Boolean,
      required: true
    },
    lastMovement: {
      type: Object,
      default: null
    },
    isMovementCompleted: {
      type: Boolean,
      default: false
    },
    status: {
      type: Object,
      required: true
    },
    transportMethod: {
      type: String,
      default: ''
    },
    selectedDriver: {
      type: Object,
      default: null
    },
    selectedSecondDriver: {
      type: Object,
      default: null
    },
    selectedGrua: {
      type: Object,
      default: null
    },
    datetime: {
      type: [String, Number],
      default: null
    },
    endDate: {
      type: [String, Number],
      default: null
    },
    deliveryDate: {
      type: [String, Number],
      default: null
    },
    receptionDatetime: {
      type: [String, Number],
      default: null
    },
    initialLocation: {
      type: [String, Number],
      default: ''
    },
    finalLocation: {
      type: [String, Number],
      default: ''
    },
    initialKm: {
      type: [String, Number],
      default: ''
    },
    finalKm: {
      type: [String, Number],
      default: ''
    },
    driverType: {
      type: String,
      default: ''
    },
    vehicleCondition: {
      type: String,
      default: ''
    },
    damageNotes: {
      type: String,
      default: ''
    },
    newDamageImages: {
      type: Array,
      default: () => []
    },
    newImagePreviews: {
      type: Array,
      default: () => []
    },
    imagesToRemove: {
      type: Array,
      default: () => []
    },
    emergencyLight: {
      type: Boolean,
      default: false
    },
    emergencyTriangle: {
      type: Boolean,
      default: false
    },
    emergencyVest: {
      type: Boolean,
      default: false
    },
    customerSignatureDataURL: {
      type: String,
      default: ''
    },
    customerSignatureReturnDataURL: {
      type: String,
      default: ''
    },
    showCustomerSignature: {
      type: Boolean,
      default: false
    },
    showCustomerSignatureReturn: {
      type: Boolean,
      default: false
    },
    hasCustomerSignature: {
      type: Boolean,
      default: false
    },
    hasCustomerSignatureReturn: {
      type: Boolean,
      default: false
    },
    additionalInfoDelivery: {
      type: String,
      default: ''
    },
    additionalInfoReception: {
      type: String,
      default: ''
    },
    showAdditionalInfo: {
      type: Boolean,
      default: false
    },
    newAdditionalImages: {
      type: Array,
      default: () => []
    },
    newAdditionalImagePreviews: {
      type: Array,
      default: () => []
    },
    savedAdditionalImages: {
      type: Array,
      default: () => []
    },
    additionalImagesToRemove: {
      type: Array,
      default: () => []
    },
    newAdditionalDocs: {
      type: Array,
      default: () => []
    },
    savedAdditionalDocs: {
      type: Array,
      default: () => []
    },
    additionalDocsToRemove: {
      type: Array,
      default: () => []
    },
    newDamageDocs: {
      type: Array,
      default: () => []
    },
    savedDamageDocs: {
      type: Array,
      default: () => []
    },
    damageDocsToRemove: {
      type: Array,
      default: () => []
    },
    validations: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    locationOptions: {
      type: Array,
      default: () => []
    },
    availableTransportOptions: {
      type: Array,
      default: () => []
    },
    vehicleTypeOptions: {
      type: Array,
      default: () => []
    },
    currentDealerId: {
      type: [String, Number],
      default: null
    },
    currentUser: {
      type: Object,
      default: null
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    documentation: {
      type: Boolean,
      default: false
    },
    chargingCable: {
      type: Boolean,
      default: false
    },
    acceptTreatment: {
      type: Boolean,
      default: false
    },
    acceptTreatmentDriver2: {
      type: Boolean,
      default: false
    },
    driver2SignatureDataURL: {
      type: String,
      default: ''
    },
    showDriver2Signature: {
      type: Boolean,
      default: false
    },
    hasDriver2Signature: {
      type: Boolean,
      default: false
    },
    driver2SignatureReturnDataURL: {
      type: String,
      default: ''
    },
    showDriver2SignatureReturn: {
      type: Boolean,
      default: false
    },
    hasDriver2SignatureReturn: {
      type: Boolean,
      default: false
    },
    selectedEquipments: {
      type: Object,
      default: () => ({})
    },
    selectedTodos: {
      type: Array,
      default: () => []
    },
    franchise: {
      type: [String, Number],
      default: null
    },
    kmLimit: {
      type: [String, Number],
      default: null
    },
    dealerFranchises: {
      type: Array,
      default: () => []
    },
    dealerKmLimits: {
      type: Array,
      default: () => []
    },
    defaultFranchiseIndex: {
      type: Number,
      default: null
    },
    defaultKmLimitIndex: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
  internalVehicleCondition: this.vehicleCondition,
      internalDamageNotes: this.damageNotes,
      // SignaturePad instances
      customerSignaturePad: null,
      showComodatoPreview: false,
      // Plantilla real del dealer (cláusulas del Grupo + overrides del dealer)
      comodatoTemplate: null,
      comodatoClauses: null,
  localAcceptTreatment: this.acceptTreatment || false,
  localAcceptTreatmentDriver2: this.acceptTreatmentDriver2 || false,
  customerSignatureReturnPad: null,
  driver2SignaturePad: null,
  driver2SignatureReturnPad: null,
      // Control local para información adicional
  localShowAdditionalInfo: this.showAdditionalInfo || false,
  // local copy of selected driver to allow immediate preselection
  localSelectedDriver: this.selectedDriver || null,
  // local copy of second driver
  localSecondDriver: this.selectedSecondDriver || null,
  localShowSecondDriver: false,
      // Alert for incomplete movements
      showIncompleteMovementsAlert: false,
      // Documentos adicionales
      tempDocs: [],
      // Documentos de daños
      tempDamageDocs: [],
      // Ubicaciones adicionales de los dealers del grupo (partners) para movimientos excepcionales
      groupPartnerLocationOptions: []
    }
  },
  created() {
    // Cargar ubicaciones de los dealers del grupo si el movimiento ya viene cargado (ej. excepcional)
    if (this.lastMovement && this.lastMovement.id && this.shouldLoadGroupPartnerLocations()) {
      this.loadGroupPartnerLocations()
    }
  },
  watch: {
    showComodatoPreview(open) {
      if (open) this.loadComodatoClauses()
    },
    vehicleCondition(newVal) {
      this.internalVehicleCondition = newVal
    },
    acceptTreatment(newVal) {
      this.localAcceptTreatment = newVal
    },
    localAcceptTreatment(newVal) {
      this.$emit('update:acceptTreatment', newVal)
    },
    acceptTreatmentDriver2(newVal) {
      this.localAcceptTreatmentDriver2 = newVal
    },
    localAcceptTreatmentDriver2(newVal) {
      this.$emit('update:acceptTreatmentDriver2', newVal)
    },
    lastMovement: {
      handler(newVal, oldVal) {
        // Si el movimiento es excepcional (o de grupo), cargar ubicaciones de los dealers del grupo
        if (newVal && newVal.id && (!oldVal || oldVal.id !== newVal.id) && this.shouldLoadGroupPartnerLocations()) {
          this.loadGroupPartnerLocations()
        }
        if (newVal && newVal.driver_id) {
          // fetch driver and emit update to parent so DriverSelector preselects it
          DriverService.view({ id: newVal.driver_id }).then(({ data }) => {
            const driver = data.data
            if (driver) {
              // set local selected driver and notify parent
              this.localSelectedDriver = driver
              this.$emit('update:selectedDriver', driver)
            }
          }).catch(err => {
            console.error('Error fetching driver for preselect:', err)
          })
        }
        // Cargar datos del segundo conductor si existen
        if (newVal && newVal.second_driver_data) {
          this.localShowSecondDriver = true
          this.localAcceptTreatmentDriver2 = !!newVal.second_driver_data.second_accept_treatment
          if (newVal.second_driver_data.second_driver_id) {
            DriverService.view({ id: newVal.second_driver_data.second_driver_id }).then(({ data }) => {
              const driver = data.data
              if (driver) {
                this.localSecondDriver = driver
                this.$emit('update:selectedSecondDriver', driver)
              }
            }).catch(err => {
              console.error('Error fetching second driver for preselect:', err)
            })
          }
          if (newVal.second_customer_signature_url) {
            this.fetchImageAsDataURL(newVal.second_customer_signature_url).then(dataURL => {
              if (dataURL) this.$emit('update:driver2SignatureDataURL', dataURL)
            })
          }
          if (newVal.second_customer_signature_return_url) {
            this.fetchImageAsDataURL(newVal.second_customer_signature_return_url).then(dataURL => {
              if (dataURL) this.$emit('update:driver2SignatureReturnDataURL', dataURL)
            })
          }
        }
        // Si el movimiento llega en estado 'reported' y no tenemos deliveryDate,
        // establecer un valor por defecto (fecha actual) y notificar al padre.
        if (newVal && newVal.status === 'reported' && (this.deliveryDate === null || this.deliveryDate === undefined)) {
          this.$emit('update:delivery-date', Math.floor(Date.now() / 1000))
        }
      },
      immediate: true
    },
    selectedDriver(newVal) {
      // keep local copy in sync when parent changes selectedDriver
      this.localSelectedDriver = newVal
    },
    localSelectedDriver(newVal) {
      // notify parent when local selection changes
      this.$emit('update:selectedDriver', newVal)
    },
    selectedSecondDriver(newVal) {
      this.localSecondDriver = newVal
    },
    localSecondDriver(newVal) {
      this.$emit('update:selectedSecondDriver', newVal)
    },
    transportMethod: {
      handler(newVal) {
        // Cuando se selecciona "YO MISMO", buscar y preseleccionar el conductor del usuario actual
        if (newVal === 'YO MISMO' && this.currentUser && this.currentUser.email) {
          // Buscar el conductor que tenga el mismo email que el usuario logueado
          DriverService.index({ 
            filter: { email: this.currentUser.email },
            expand: 'dealer'
          }).then(({ data }) => {
            if (data && data.data && data.data.length > 0) {
              const driver = data.data[0]
              this.localSelectedDriver = driver
              this.$emit('update:selectedDriver', driver)
            } else {
              console.warn('⚠️ No se encontró conductor con email:', this.currentUser.email)
            }
          }).catch(err => {
            console.error('❌ Error buscando conductor para "YO MISMO":', err)
          })
        }
      },
      immediate: true
    },
    damageNotes(newVal) {
      this.internalDamageNotes = newVal
    },
    showAdditionalInfo(newVal) {
      this.localShowAdditionalInfo = newVal
    },
    localShowAdditionalInfo(newVal) {
      this.$emit('update:showAdditionalInfo', newVal)
    },
    vehicleTypeOptions: {
      handler(newOptions) {
        // Auto-seleccionar el tipo de vehículo apropiado cuando las opciones cambian
        if (newOptions && newOptions.length > 0 && !this.driverType) {
          const enabledOptions = newOptions.filter(opt => !opt.disabled)
          
          // Si el vehículo está matriculado (tiene plate_number), pre-seleccionar "MATRICULADO"
          if (this.status && this.status.plate_number) {
            const matriculadoOption = enabledOptions.find(opt => 
              opt.value === 'MATRICULADO' || opt.value === 'matriculado'
            )
            if (matriculadoOption) {
              this.$emit('update:driverType', matriculadoOption.value)
              return
            }
          }
          
          // Si solo hay una opción habilitada, seleccionarla automáticamente
          if (enabledOptions.length === 1) {
            this.$emit('update:driverType', enabledOptions[0].value)
          }
        }
      },
      immediate: true
    },
    'status.plate_number': {
      handler() {
        // Si cambia el estado de matriculación y no hay tipo seleccionado, pre-seleccionar
        if (!this.driverType && this.vehicleTypeOptions && this.vehicleTypeOptions.length > 0) {
          const enabledOptions = this.vehicleTypeOptions.filter(opt => !opt.disabled)
          
          if (this.status && this.status.plate_number) {
            const matriculadoOption = enabledOptions.find(opt => 
              opt.value === 'MATRICULADO' || opt.value === 'matriculado'
            )
            if (matriculadoOption) {
              this.$emit('update:driverType', matriculadoOption.value)
            }
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    resolvedLocationOptions() {
      const combined = [...this.locationOptions]
      this.groupPartnerLocationOptions.forEach(loc => {
        if (!combined.some(x => x.id === loc.id)) {
          combined.push(loc)
        }
      })
      return combined
    },
    franchiseOptions() {
      if (!this.dealerFranchises || this.dealerFranchises.length === 0) return []
      
      return this.dealerFranchises.map((value, index) => ({
        value: value,
        text: `${value}€`,
        isDefault: this.defaultFranchiseIndex === index
      }))
    },
    kmLimitOptions() {
      if (!this.dealerKmLimits || this.dealerKmLimits.length === 0) return []
      
      return this.dealerKmLimits.map((value, index) => ({
        value: value,
        text: `${value} km`,
        isDefault: this.defaultKmLimitIndex === index
      }))
    },
    comodatoHtml() {
      if (!this.lastMovement || !this.status) return ''

      const movement = this.lastMovement
      const vehicle = this.status
      const dealer = vehicle.dealer || {}
      const comodatoConfig = dealer.comodato_config || {}
      const driver = this.localSelectedDriver || this.selectedDriver

      // Helpers de formato
      const formatDate = (ts) => {
        if (!ts) return '____________________'
        const n = Number(ts)
        const d = n < 1e12 ? new Date(n * 1000) : new Date(n)
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
      }
      const formatTime = (ts) => {
        if (!ts) return '_______________'
        const n = Number(ts)
        const d = n < 1e12 ? new Date(n * 1000) : new Date(n)
        return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      }
      const formatCurrency = (val) => {
        if (val === null || val === undefined || val === '') return '___'
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(val)
      }

      // Datos del cesionario
      let cesionarioBlock = ''
      if (driver) {
        const docType = (driver.document_type || 'DNI').toUpperCase()
        const expDate = driver.document_expiration_date
          ? new Date(driver.document_expiration_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : '____________________'
        cesionarioBlock = `
      <p>
        Y de otra <u>${driver.name} ${driver.lastname || ''}</u> número ${docType}
        <u>${driver.document_number || ''}</u>, Licencia de conducción en vigor hasta <u>${expDate}</u>
        (según fotocopia adjunta), en calidad de Cesionario.
      </p>`
      } else if (this.currentUser) {
        cesionarioBlock = `
      <p>
        Y de otra <u>${this.currentUser.name || ''} ${this.currentUser.lastname || ''}</u> en calidad de Cesionario.
      </p>`
      } else {
        cesionarioBlock = `<p>Y de otra <u>____________________</u> en calidad de Cesionario.</p>`
      }

      // Datos del segundo conductor
      let secondDriverBlock = ''
      const secondDriver = this.selectedSecondDriver || this.localSecondDriver
      if (secondDriver) {
        const docType2 = (secondDriver.document_type || 'DNI').toUpperCase()
        const expDate2 = secondDriver.document_expiration_date
          ? new Date(secondDriver.document_expiration_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : '____________________'
        secondDriverBlock = `
      <p>
        Y de otra <u>${secondDriver.name} ${secondDriver.lastname || ''}</u> número ${docType2}
        <u>${secondDriver.document_number || ''}</u>, Licencia de conducción en vigor hasta <u>${expDate2}</u>
        (según fotocopia adjunta), en calidad de Segundo Cesionario.
      </p>`
      }

      // Bloque devolución
      const isCompleted = movement.status === 'completed'
      let devolucionBlock = ''
      if (isCompleted && movement.reception_datetime) {
        devolucionBlock = `
      <p>Día <u>${formatDate(movement.reception_datetime)}</u>,
      hora <u>${formatTime(movement.reception_datetime)}</u>,
      Km. <u>${movement.end_km || '___'}</u></p>
      <table style="width: 100%; text-align: center; margin-top: 60px;">
        <tr>
          <td>Fdo. Cedente</td>
          <td>Fdo. Cesionario<br>${this.customerSignatureReturnDataURL
            ? `<img src="${this.customerSignatureReturnDataURL}" alt="Firma devolución" style="width: auto; height: 80px; border: 1px solid #ccc;">`
            : '____________________'
          }</td>
        </tr>
        ${secondDriver ? `<tr>
          <td></td>
          <td style="padding-top: 30px;"><strong>Fdo. Segundo Cesionario</strong><br>${this.driver2SignatureReturnDataURL
            ? `<img src="${this.driver2SignatureReturnDataURL}" alt="Firma devolución segundo conductor" style="width: auto; height: 80px; border: 1px solid #ccc;">`
            : '____________________'
          }</td>
        </tr>` : ''}
      </table>`
      } else {
        devolucionBlock = `
      <p>Día <u>____________________</u>, hora <u>_______________</u>, Km. <u>___________</u></p>
      <table style="width: 100%; text-align: center; margin-top: 60px;">
        <tr>
          <td>Fdo. Cedente</td>
          <td>Fdo. Cesionario<br>____________________</td>
        </tr>
        ${secondDriver ? `<tr>
          <td></td>
          <td style="padding-top: 30px;"><strong>Fdo. Segundo Cesionario</strong><br>____________________</td>
        </tr>` : ''}
      </table>`
      }

      // Cláusulas reales de la plantilla del dealer; si aún no han cargado, el texto base
      const fallbackClausesHtml = `
    <ol>
      <li><strong>Cesión:</strong> El Cedente entrega el Vehículo al Cesionario para que lo destine al uso indicado en el Expositivo Primero.</li>
      <li><strong>Carácter Intuitu Personae:</strong> La cesión del uso del Vehículo es de carácter personalísimo, por lo que sólo podrá ser conducido por el Cesionario.</li>
      <li><strong>Plazo:</strong> El plazo de la cesión se inicia a las
        <u>${formatTime(movement.delivery_date || movement.datetime)}</u>
        del día de hoy y finaliza el día
        <u>${movement.end_date ? formatDate(movement.end_date) : '____________________'}</u>.
      </li>
      <li><strong>Contraprestación:</strong> Para conocer las condiciones por concepto de desgaste y depreciación del vehículo por su uso, póngase en contacto con su concesión.</li>
      <li><strong>Seguro:</strong> El Cedente tiene concertada sobre el Vehículo una póliza de seguro combinado con
        una franquicia de <u>${formatCurrency(movement.franchise ?? this.franchise)}</u>.
        En caso de siniestro el Cesionario deberá satisfacer dicha franquicia al Cedente. Por los riesgos no cubiertos por la póliza, el Cesionario se hará responsable en los supuestos de culpa o negligencia.
      </li>
      <li><strong>Sanciones e infracciones:</strong> Todas las sanciones y multas por infracciones de tráfico cometidas durante la tenencia del vehículo por parte del Cesionario serán a su cargo. Los datos personales del Cesionario serán facilitados a la Administración si ésta lo requiere, según la normativa vigente.</li>
      <li><strong>Límite de Kilometraje:</strong> El kilometraje máximo sin cargo adicional es de
        <u>${movement.km_limit ?? this.kmLimit ?? '____'}</u> Kms.
        En caso de excederse el límite de kilómetros señalado, el Cedente podrá cobrar al Cesionario
        ${formatCurrency(comodatoConfig.euros_per_km_exceeded)} por Km excedido.
      </li>
      <li><strong>Límite geográfico:</strong> Queda expresamente prohibida la circulación, utilización, traslado o salida del
        vehículo objeto del presente contrato fuera del territorio nacional español, salvo autorización previa,
        expresa y por escrito de la empresa prestataria. El incumplimiento de esta obligación constituirá un
        incumplimiento grave del contrato y facultará a la empresa prestataria para adoptar las medidas que
        estime oportunas, incluyendo la resolución inmediata del contrato, la reclamación de los daños y
        perjuicios ocasionados, así como la repercusión al arrendatario de cualquier coste, sanción, gasto de
        recuperación, asistencia o perjuicio derivado de la salida no autorizada del vehículo fuera de España.
        Asimismo, el arrendatario reconoce que cualquier cobertura de seguro, asistencia o garantía podrá
        verse limitada o anulada en caso de incumplimiento de la presente cláusula.
      </li>
      <li><strong>Datos Personales:</strong> De acuerdo con la Ley Orgánica 15/1999, los datos del Cesionario serán incorporados a un fichero del Cedente para gestionar este contrato y enviar información comercial. El Cesionario podrá ejercer sus derechos de acceso, rectificación, cancelación y oposición dirigiéndose al Cedente.</li>
      <li><strong>Jurisdicción y Fuero:</strong> Las partes se someten a los Juzgados y Tribunales de <u>${dealer.city || '____'} Ciudad</u>.</li>
    </ol>
  `
      const clausesHtml = this.comodatoClauses
        ? this.renderTemplateClauses(this.comodatoClauses, this.getComodatoVariables({ movement, vehicle, dealer, comodatoConfig, driver, formatDate, formatTime, formatCurrency }))
        : fallbackClausesHtml
      const documentTitle = (this.comodatoTemplate && this.comodatoTemplate.title) || 'Contrato de cesión de uso de vehículo de cortesía; sustitución o prueba'

      return `
    <h2 style="text-align: center; text-decoration: underline;">
      ${documentTitle}
    </h2>

    <p><strong>En ${dealer.city || '____'} ${formatDate(movement.delivery_date || movement.datetime)}</strong></p>

    <h3 style="text-align: center">REUNIDOS</h3>

    <p>
      De una parte <u>${comodatoConfig.company_name || dealer.name || '____'}</u>,
      CIF <u>${comodatoConfig.cif || '____'}</u> y domicilio
      <u>${comodatoConfig.company_address || '____'}</u> en calidad de Cedente,
    </p>

    ${cesionarioBlock}
    ${secondDriverBlock}

    <p><strong>I.</strong> El Cesionario es cliente del Cedente y ha solicitado al Cedente, cuya actividad es la venta y reparación de vehículos:</p>
    <ul>
      <li>El uso de un vehículo de sustitución mientras se lleva a cabo sobre el suyo la reparación encargada al Cedente.</li>
      <li>Realizar una prueba sobre un vehículo pues tiene interés en adquirir uno de igual o similares características.</li>
    </ul>

    <p>
      <strong>II.</strong> El Cedente dispone del vehículo marca
      <u>${vehicle.brand || ''} ${vehicle.model || ''}</u> matrícula
      <u>${vehicle.plate_number || '____'}</u> para su uso como vehículo de sustitución o prueba según corresponda
      (en adelante el Vehículo). El contador de dicho vehículo indica haber recorrido
      <u>${movement.initial_km || '____'}</u> Kms. en el momento de la entrega.
    </p>

    <p><strong>III.</strong> Que de conformidad con lo hasta aquí expuesto, las partes</p>
    <br>
    <h3 style="text-align: center">ACUERDAN</h3>
    ${clausesHtml}

    <p>Y en prueba de conformidad, las partes firman el presente contrato por duplicado y a un solo efecto en el lugar y fecha al principio indicados.</p>

    <br><br>

    <table style="width: 100%; text-align: center;">
      <tr>
        <td style="text-align: left; width: 50%;">
          <strong>EL CEDENTE</strong><br>
          Sello<br><br>
          Fdo. D. ______________________________
        </td>
        <td style="text-align: left; width: 50%;">
          <strong>EL CESIONARIO</strong><br>
          Firma<br>
          ${this.customerSignatureDataURL
          ? `<img src="${this.customerSignatureDataURL}" alt="Firma" style="width: auto; height: 100px; border: 1px solid #ccc;">`
          : '<br><br><br>'
        }
          <br>
          Fdo. D. ${driver ? `${driver.name} ${driver.lastname || ''}` : (this.currentUser ? `${this.currentUser.name || ''} ${this.currentUser.lastname || ''}` : '______________________________')}
          ${secondDriver ? `<br><br><strong>SEGUNDO CESIONARIO</strong><br>Firma<br>${
            this.driver2SignatureDataURL
            ? `<img src="${this.driver2SignatureDataURL}" alt="Firma segundo conductor" style="width: auto; height: 100px; border: 1px solid #ccc;">`
            : '<br><br><br>'
          }<br>Fdo. D. ${secondDriver.name} ${secondDriver.lastname || ''}` : ''}
        </td>
      </tr>
    </table>

    <br><br>

    <div style="border: 1px solid black; padding: 16px;">
      <p style="text-align: center"><strong>Devolución del vehículo</strong></p>
      ${devolucionBlock}
    </div>
  `
    },
    shouldShowCustomerSignature() {
      const shouldShow = this.lastMovement && this.lastMovement.type === 'assignment' && (this.lastMovement.status === 'unreported' || this.lastMovement.status === 'reported')
      return shouldShow
    },
    shouldShowCustomerSignatureReturn() {
      return this.lastMovement && this.lastMovement.type === 'assignment' && this.lastMovement.status === 'processing'
    },
    vehicleInfo() {
      if (!this.lastMovement) return null
      return this.lastMovement.vehiculo
    },
    movementInfo() {
      if (!this.lastMovement) return null
      return {
        id: this.lastMovement.id,
        type: this.lastMovement.tipo,
        status: this.lastMovement.estado,
        dealer: this.lastMovement.dealer,
        vehiculo: this.lastMovement.vehiculo,
        datetime: this.lastMovement.fechaHora,
        initialLocation: this.lastMovement.ubicacionInicial,
        finalLocation: this.lastMovement.ubicacionFinal,
        initialKm: this.lastMovement.kmInicial,
        finalKm: this.lastMovement.kmFinal,
        observations: this.lastMovement.observaciones
      }
    },
    isReturnMovement() {
      return this.lastMovement && this.lastMovement.tipo === 'return'
    },
    isExitMovement() {
      return this.lastMovement && this.lastMovement.tipo === 'exit'
    },
    shouldShowGruaSelector() {
      return this.transportMethod === 'grua'
    },
    shouldShowDriverSelector() {
      return this.transportMethod === 'propio' || this.transportMethod === 'grua'
    },
    requiresDamageImages() {
      return this.vehicleCondition === 'damages'
    },
    hasRequiredImages() {
      if (!this.requiresDamageImages) return true
      // Si hay daños, verificar que haya al menos una imagen (nueva o guardada)
      const hasSavedImages = this.formattedSavedImages && this.formattedSavedImages.length > 0
      const hasNewImages = this.newDamageImages && this.newDamageImages.length > 0
      return hasSavedImages || hasNewImages
    },
    // Validar si los kilómetros iniciales son inválidos (menores que current_kms)
    isInitialKmInvalid() {
      // No validar si el movimiento está completado o en processing (campo deshabilitado)
      if (this.isMovementCompleted || (this.lastMovement && this.lastMovement.status === 'processing')) {
        return false
      }
      
      if (!this.initialKm || !this.status || !this.status.current_kms) return false
      const initialKmFloat = parseFloat(this.initialKm)
      const currentKmsFloat = parseFloat(this.status.current_kms)
      return initialKmFloat < currentKmsFloat
    },
    initialKmErrorMessage() {
      if (!this.initialKm || this.initialKm === '') {
        return 'Los kilómetros iniciales son obligatorios'
      }
      if (this.isInitialKmInvalid) {
        return `Los kilómetros iniciales han de ser mayor a ${this.status.current_kms} km (kilómetros de la recepción del movimiento anterior)`
      }
      return ''
    },
    // Validar si los kilómetros finales son inválidos (menores o iguales a los iniciales)
    isFinalKmInvalid() {
      if (!this.finalKm || !this.initialKm) return false

      return parseFloat(this.finalKm) < parseFloat(this.initialKm)
    },
    finalKmErrorMessage() {
      if (!this.finalKm || this.finalKm === '') {
        return 'Los kilómetros finales son obligatorios'
      }
      if (this.isFinalKmInvalid) {
        return 'Los kilómetros finales deben ser mayores que los iniciales'
      }
      return ''
    },
    // Validar si el conductor es obligatorio para cesiones con transporte CLIENTE
    isDriverRequiredForAssignment() {
      if (!this.lastMovement) return false
      return this.lastMovement.type === 'assignment' && this.transportMethod === 'CLIENTE'
    },
    isDriverMissing() {
      if (!this.isDriverRequiredForAssignment) return false
      return !this.selectedDriver && !this.localSelectedDriver
    },
    // Determina qué campo de información adicional usar según el status del movimiento
    currentAdditionalInfo() {
      // Si el status es 'processing', usar additionalInfoReception
      if (this.lastMovement && this.lastMovement.status === 'processing') {
        return this.additionalInfoReception
      }
      // Si el status es 'reported' o cualquier otro, usar additionalInfoDelivery
      return this.additionalInfoDelivery
    },
    // Label dinámico según el status
    additionalInfoLabel() {
      if (this.lastMovement && this.lastMovement.status === 'processing') {
        return 'Información adicional de recepción'
      }
      return 'Información adicional de entrega'
    },
    // Hint dinámico según el status
    additionalInfoHint() {
      if (this.lastMovement && this.lastMovement.status === 'processing') {
        return 'Agregar cualquier información relevante sobre la recepción del vehículo (los saltos de línea se preservarán)'
      }
      return 'Agregar cualquier información relevante sobre la entrega del movimiento (los saltos de línea se preservarán)'
    },
    formattedSavedAdditionalImages() {
      // Get additional images from lastMovement, similar to how damage images work
      if (!this.lastMovement || !this.lastMovement.additionalImagesUrls) {
        return []
      }
      
      // Si ya es un array y tiene el formato correcto
      if (Array.isArray(this.lastMovement.additionalImagesUrls)) {
        if (this.lastMovement.additionalImagesUrls.length > 0 && 
            typeof this.lastMovement.additionalImagesUrls[0] === 'object') {
          return this.lastMovement.additionalImagesUrls
        }
        // Si son solo URLs como strings
        return this.lastMovement.additionalImagesUrls.map((url, index) => ({
          id: index,
          url: url
        }))
      }
      
      // Si es un objeto con índices numéricos como claves (ej: {"1": {id: 22, url: "..."}, "2": {...}})
      if (typeof this.lastMovement.additionalImagesUrls === 'object') {
        return Object.values(this.lastMovement.additionalImagesUrls).filter(img => img && img.url)
      }
      
      return []
    },
    formattedSavedAdditionalDocs() {
      // Get additional documents from lastMovement
      if (!this.lastMovement || !this.lastMovement.additionalDocsUrls) {
        return []
      }
      
      // Si ya es un array y tiene el formato correcto
      if (Array.isArray(this.lastMovement.additionalDocsUrls)) {
        if (this.lastMovement.additionalDocsUrls.length > 0 && 
            typeof this.lastMovement.additionalDocsUrls[0] === 'object') {
          return this.lastMovement.additionalDocsUrls
        }
        // Si son solo URLs como strings
        return this.lastMovement.additionalDocsUrls.map((url, index) => ({
          id: index,
          url: url,
          name: url.split('/').pop()
        }))
      }
      
      // Si es un objeto con índices numéricos como claves
      if (typeof this.lastMovement.additionalDocsUrls === 'object') {
        return Object.values(this.lastMovement.additionalDocsUrls).filter(doc => doc && doc.url)
      }
      
      return []
    },
    canAddMoreDocs() {
      return this.remainingDocSlots > 0
    },
    remainingDocSlots() {
      const savedCount = this.formattedSavedAdditionalDocs ? this.formattedSavedAdditionalDocs.length - this.additionalDocsToRemove.length : 0
      const newCount = this.newAdditionalDocs ? this.newAdditionalDocs.length : 0
      return Math.max(0, 4 - savedCount - newCount)
    },
    formattedSavedDamageDocs() {
      // Get damage documents from lastMovement
      if (!this.lastMovement || !this.lastMovement.damageDocsUrls) {
        return []
      }
      
      // Si ya es un array y tiene el formato correcto
      if (Array.isArray(this.lastMovement.damageDocsUrls)) {
        if (this.lastMovement.damageDocsUrls.length > 0 && 
            typeof this.lastMovement.damageDocsUrls[0] === 'object') {
          return this.lastMovement.damageDocsUrls
        }
        // Si son solo URLs como strings
        return this.lastMovement.damageDocsUrls.map((url, index) => ({
          id: index,
          url: url,
          name: url.split('/').pop()
        }))
      }
      
      // Si es un objeto con índices numéricos como claves
      if (typeof this.lastMovement.damageDocsUrls === 'object') {
        return Object.values(this.lastMovement.damageDocsUrls).filter(doc => doc && doc.url)
      }
      
      return []
    },
    canAddMoreDamageDocs() {
      return this.remainingDamageDocSlots > 0
    },
    remainingDamageDocSlots() {
      const savedCount = this.formattedSavedDamageDocs ? this.formattedSavedDamageDocs.length - this.damageDocsToRemove.length : 0
      const newCount = this.newDamageDocs ? this.newDamageDocs.length : 0
      return Math.max(0, 4 - savedCount - newCount)
    },
    canComplete() {
      if (!this.lastMovement) {
        return false
      }
      
      // Validar campos obligatorios básicos
      // const hasBasicFields = this.vehicleCondition && this.hasRequiredImages
      const hasBasicFields = this.vehicleCondition
      
      // Validar que el estado de matrícula sea obligatorio (no vacío)
      if (!this.driverType || this.driverType === '') {
        return false
      }
      
      // Validar que si es transporte con grúa, debe tener una grúa seleccionada
      if (this.transportMethod === 'GRUA' && (this.lastMovement?.status === 'reported' || this.lastMovement?.status === 'processing') && !this.selectedGrua) {
        return false
      }
      
      // Validar conductor obligatorio para cesiones con transporte CLIENTE
      if (this.isDriverRequiredForAssignment && this.isDriverMissing) {
        return false
      }
      
      // Validar que si es transporte con conductor (pero no CLIENTE en cesiones, ya validado arriba), debe tener un conductor seleccionado
      if (((this.transportMethod === 'YO MISMO' && this.lastMovement.status === 'reported')) && 
          !this.selectedDriver) {
        return false
      }
      
      // Si el movimiento está en estado 'reported', validar kilómetros iniciales
      if (this.lastMovement.status === 'reported') {
        // Validar kilómetros iniciales
        if (!this.initialKm || this.initialKm === '') {
          return false
        }
        
        // Validar que los kilómetros iniciales sean mayores o iguales a los actuales
        if (this.isInitialKmInvalid) {
          return false
        }
        
        // Validar firma de entrega obligatoria SOLO para cesiones (assignment)
        if (this.lastMovement.type === 'assignment' && this.shouldShowCustomerSignature && !this.customerSignatureDataURL) {
          return false
        }
        
        // Validar firma del segundo conductor si está activo
        if (this.lastMovement.type === 'assignment' && this.shouldShowDriver2Signature && !this.driver2SignatureDataURL) {
          return false
        }
      }
      
      // Si el movimiento está en estado 'processing', validar kilómetros finales
      if (this.lastMovement.status === 'processing') {
        // Validar kilómetros finales
        if (!this.finalKm || this.finalKm === '') {
          return false
        }
        
        // Validar que los kilómetros finales sean mayores que los iniciales
        if (this.initialKm && this.finalKm && parseFloat(this.finalKm) < parseFloat(this.initialKm)) {
          return false
        }
        
        // Validar firma de recepción obligatoria SOLO para cesiones (assignment)
        if (this.lastMovement.type === 'assignment' && this.shouldShowCustomerSignatureReturn && !this.customerSignatureReturnDataURL) {
          return false
        }
        
        // Validar firma de recepción del segundo conductor si está activo
        if (this.lastMovement.type === 'assignment' && this.shouldShowDriver2SignatureReturn && !this.driver2SignatureReturnDataURL) {
          return false
        }
      }

      if (this.lastMovement.type === 'assignment' && this.lastMovement.status === 'reported') {
        const effectiveFranchise = this.franchise ?? null
        const effectiveKmLimit = this.kmLimit ?? null
        if (effectiveFranchise === null || effectiveFranchise === undefined || effectiveFranchise === '') {
          return false
        }
        if (effectiveKmLimit === null || effectiveKmLimit === undefined || effectiveKmLimit === '') {
          return false
        }
      }
      return hasBasicFields
    },
    shouldShowImageManager() {
      return this.requiresDamageImages
    },
    formattedDateTime() {
      return this.datetime ? moment(this.datetime).format('DD/MM/YYYY HH:mm') : ''
    },
    formattedEndDate() {
      return this.endDate ? moment(this.endDate).format('DD/MM/YYYY HH:mm') : ''
    },
    formattedReceptionDate() {
      return this.receptionDatetime ? moment(this.receptionDatetime).format('DD/MM/YYYY HH:mm') : ''
    },
    transportMethodText() {
      const methods = {
        'propio': 'Transporte Propio',
        'grua': 'Grúa',
        'terceros': 'Terceros'
      }
      return methods[this.transportMethod] || this.transportMethod
    },
    vehicleConditionText() {
      const conditions = {
        'excellent': 'Excelente',
        'good': 'Bueno',
        'fair': 'Regular',
        'poor': 'Malo'
      }
      return conditions[this.vehicleCondition] || this.vehicleCondition
    },
    equipmentStatus() {
      const equipment = []
      if (this.emergencyLight) equipment.push('Luz de emergencia')
      if (this.emergencyTriangle) equipment.push('Triángulo')
      if (this.emergencyVest) equipment.push('Chaleco')
      return equipment.length > 0 ? equipment.join(', ') : 'Ninguno'
    },
    hasCustomerSignatureData() {
      return this.customerSignaturePad && !this.customerSignaturePad.isEmpty()
    },
    hasCustomerSignatureReturnData() {
      return this.customerSignatureReturnPad && !this.customerSignatureReturnPad.isEmpty()
    },
    hasDriver2SignatureData() {
      return this.driver2SignaturePad && !this.driver2SignaturePad.isEmpty()
    },
    hasDriver2SignatureReturnData() {
      return this.driver2SignatureReturnPad && !this.driver2SignatureReturnPad.isEmpty()
    },
    shouldShowDriver2Signature() {
      return this.lastMovement && this.lastMovement.type === 'assignment' && (this.lastMovement.status === 'unreported' || this.lastMovement.status === 'reported') && this.localShowSecondDriver
    },
    shouldShowDriver2SignatureReturn() {
      return this.lastMovement && this.lastMovement.type === 'assignment' && this.lastMovement.status === 'processing' && this.localShowSecondDriver
    },
    formattedSavedImages() {
      if (!this.lastMovement || !this.lastMovement.imagesUrls) {
        return []
      }
      
      // Verificar si ya tienen el formato correcto (array de objetos con url)
      if (Array.isArray(this.lastMovement.imagesUrls) && 
          this.lastMovement.imagesUrls.length > 0 && 
          typeof this.lastMovement.imagesUrls[0] === 'object') {
        return this.lastMovement.imagesUrls
      }
      
      // Si son solo URLs como strings, convertir al formato esperado
      if (Array.isArray(this.lastMovement.imagesUrls)) {
        return this.lastMovement.imagesUrls.map((url, index) => ({
          id: index,
          url: url
        }))
      }
      
      return []
    },
    vehicleIdNumber() {
      // Devuelve solo el número del id, aunque venga como '85_mov_308' u otro string
      const id = this.status.id || this.status.vehicle_id
      if (!id) return null
      
      const num = parseInt(id.toString().replace(/_mov_.+$/, ''))
      return isNaN(num) ? null : num
    }
  },
  methods: {
    // Carga el clausulado real del dealer del vehículo (plantilla del Grupo + sus overrides)
    async loadComodatoClauses() {
      const dealerId = this.status && this.status.dealer && this.status.dealer.id
      if (!dealerId) return
      try {
        const response = await ComodatoClauseService.getClauses(dealerId)
        const data = response.data.data || response.data
        this.comodatoTemplate = data.template || null
        // Las opcionales desactivadas no salen en el documento
        this.comodatoClauses = (data.clauses || []).filter(c => c.type !== 'opcional' || c.enabled)
      } catch (error) {
        console.error('Error al cargar el clausulado del comodato:', error)
        this.comodatoClauses = null
        this.$toast.warning('No se pudo cargar el clausulado del dealer; se muestra el texto base.')
      }
    },

    // Valores reales de cada variable {{...}} para este movimiento
    getComodatoVariables({ movement, vehicle, dealer, comodatoConfig, driver, formatDate, formatTime, formatCurrency }) {
      const dateTime = ts => ts ? `${formatDate(ts)}, ${formatTime(ts)}` : '____________'
      const kms = v => (v !== null && v !== undefined && v !== '') ? `${v} Kms` : '____________'
      const signature = (url, alt) => url
        ? `<img src="${url}" alt="${alt}" style="width: auto; height: 60px; border: 1px solid #ccc; vertical-align: middle;">`
        : '____________'
      const expiration = driver && driver.document_expiration_date
        ? new Date(driver.document_expiration_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '____________'
      const euros = comodatoConfig.euros_per_km_exceeded
      const deliveryTs = movement.delivery_date || movement.datetime
      return {
        'Nombre dealer': comodatoConfig.company_name || dealer.name || '____',
        'Ciudad dealer': comodatoConfig.company_city || dealer.city || '____',
        'CIF dealer': comodatoConfig.cif || '____',
        'Domicilio dealer': comodatoConfig.company_address || '____',
        'Nombre conductor': driver ? `${driver.name} ${driver.lastname || ''}`.trim() : '____________',
        'DNI conductor': (driver && driver.document_number) || '____________',
        'Fecha caducidad carnet de conducir': expiration,
        'Marca del vehículo': `${vehicle.brand || ''} ${vehicle.model || ''}`.trim() || '____',
        'Matrícula vehículo': vehicle.plate_number || '____',
        'Km entrega': kms(movement.initial_km),
        'Fecha y hora de entrega': dateTime(deliveryTs),
        'Franquicia': formatCurrency(movement.franchise ?? this.franchise),
        'Límites de km diarios': kms(movement.km_limit ?? this.kmLimit),
        'Euros por km excedido': (euros !== null && euros !== undefined && euros !== '')
          ? `${Number(euros).toLocaleString('es-ES')} €/Km` : '___',
        'Fecha y hora de devolución': movement.status === 'completed' ? dateTime(movement.reception_datetime) : '____________',
        'Firma de la ENTREGA del Cedente': { html: '____________' },
        'Firma de la ENTREGA del Cesionario': { html: signature(this.customerSignatureDataURL, 'Firma entrega') },
        'Firma de la DEVOLUCIÓN del Cedente': { html: '____________' },
        'Firma de la DEVOLUCIÓN del Cesionario': { html: signature(this.customerSignatureReturnDataURL, 'Firma devolución') }
      }
    },

    // Lista numerada con el texto real de cada cláusula y sus variables sustituidas
    renderTemplateClauses(clauses, values) {
      const esc = t => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const fill = text => esc(text || '')
        .replace(/\{\{([^}]+)\}\}/g, (m, name) => {
          const key = name.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim()
          const val = values[key]
          if (val === undefined) return '____________'
          return (val && val.html !== undefined) ? val.html : `<u>${esc(val)}</u>`
        })
        .replace(/\n/g, '<br>')
      const items = clauses.map(c => `<li><strong>${esc(c.title)}:</strong> ${fill(c.text)}</li>`).join('')
      return `<ol>${items}</ol>`
    },

    shouldLoadGroupPartnerLocations() {
      // Solo cargar las ubicaciones de los dealers del grupo (partners) cuando
      // se está creando/procesando un movimiento excepcional
      const m = this.lastMovement
      return !!(m && (m.is_excepcional === true || m.is_excepcional === 1 || m.is_excepcional === '1'))
    },
// Cargar las ubicaciones de los dealers del grupo (partners)
    async loadGroupPartnerLocations() {
      const sourceId = this.lastMovement && this.lastMovement.source_location_id
      const destId = this.lastMovement && this.lastMovement.destination_location_id

      // GrupoService se carga dinámicamente para evitar binding indefinido por dependencia circular
      const GrupoServiceRef = await import(/* webpackChunkName: "grupo-service" */ '../services/GrupoService').then(m => m.default).catch(() => null)
      if (!GrupoServiceRef || typeof GrupoServiceRef.index !== 'function') {
        console.error('[MovementProcessingView] GrupoService no disponible (import dinámico falló), abortando.')
        return
      }

      try {
        let currentDealerId = this.currentUser && this.currentUser.dealer ? this.currentUser.dealer.id : null
        if (!currentDealerId && this.status && this.status.dealer_id) {
          currentDealerId = this.status.dealer_id
        }
        if (!currentDealerId) {
          return
        }

        const response = await GrupoServiceRef.index({ expand: 'dealerships' })
        const groups = (response.data && response.data.data) || []
        const dealers = []
        groups.forEach(group => {
          const groupDealers = group.dealerships || []
          const isMember = currentDealerId && groupDealers.some(d => d.id === currentDealerId)
          if (isMember) {
            groupDealers.forEach(d => {
              if (d.id !== currentDealerId && !dealers.some(x => x.id === d.id)) {
                dealers.push({ id: d.id, name: d.name })
              }
            })
          }
        })

        // Cargar las ubicaciones de cada dealer partner y fusionarlas en groupPartnerLocationOptions
        for (const dealer of dealers) {
          const params = {
            'id': dealer.id,
            expand: 'dealer',
            'per-page': 100
          }
          const dealerResponse = await LocationService.getLocationDealer(params)
          let locations = []
          if (dealerResponse.data && dealerResponse.data.data && Array.isArray(dealerResponse.data.data.locations)) {
            locations = dealerResponse.data.data.locations
          } else if (dealerResponse.data && dealerResponse.data.data && Array.isArray(dealerResponse.data.data)) {
            locations = dealerResponse.data.data
          }

          locations.forEach(location => {
            if (!this.groupPartnerLocationOptions.some(loc => loc.id === location.id)) {
              this.groupPartnerLocationOptions.push({
                id: location.id,
                name: location.name,
                displayName: location.dealer_name ? `${location.name} (${location.dealer_name})` : location.name,
                dealer_id: location.dealer_id,
                is_own: false
              })
            }
          })
        }

          const finalIds = this.resolvedLocationOptions.map(l => l.id)
          console.log('[MovementProcessingView] UBICACIONES FINAL disponibles (ids):', JSON.stringify(finalIds),
          '| origen en opciones:', finalIds.includes(sourceId),
          '| destino en opciones:', finalIds.includes(destId))
      } catch (error) {
        console.error('[MovementProcessingView] Error al cargar las ubicaciones de los dealers del grupo:', error)
        if (typeof this.handleApiError === 'function') {
          this.handleApiError(error, 'Error al cargar las ubicaciones de los dealers del grupo')
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'No especificada'
      return moment(dateString).format('DD/MM/YYYY HH:mm')
    },
    getLocationName(location) {
      if (!location) return 'No especificada'
      if (typeof location === 'object') {
        return location.displayName || location.name || 'Ubicación'
      }
      // Buscar en resolvedLocationOptions (incluye ubicaciones de dealers del grupo)
      const found = this.resolvedLocationOptions.find(loc => loc.id === location)
      return found ? found.displayName : location
    },
    getDriverName(driver) {
      if (!driver) return 'No especificado'
      if (typeof driver === 'object') {
        return `${driver.name} ${driver.lastname}`.trim()
      }
      return driver
    },
    getGruaName(grua) {
      if (!grua) return 'No especificada'
      if (typeof grua === 'object') {
        return grua.company || grua.name || 'Empresa de grúa'
      }
      return grua
    },
    handleImageUpload(images) {
      this.$emit('update:newDamageImages', images)
    },
    handleImageRemove(images) {
      this.$emit('update:imagesToRemove', images)
    },
    handleVehicleConditionChange(condition) {
      this.$emit('update:vehicleCondition', condition)
    },
    handleDamageNotesChange(notes) {
      this.$emit('update:damageNotes', notes)
    },
    handleAdditionalInfoInput(value) {
      // Emitir al campo correcto según el status del movimiento
      if (this.lastMovement && this.lastMovement.status === 'processing') {
        this.$emit('update:additionalInfoReception', value)
      } else {
        this.$emit('update:additionalInfoDelivery', value)
      }
    },
    handleTransportMethodChange(method) {
      this.$emit('update:transportMethod', method)
    },
    handleDriverChange(driver) {
      this.$emit('update:selectedDriver', driver)
    },
    handleGruaChange(grua) {
      this.$emit('update:selectedGrua', grua)
    },
    handleDateTimeChange(datetime) {
      this.$emit('update:datetime', datetime)
    },
    handleEndDateChange(endDate) {
      this.$emit('update:endDate', endDate)
    },
    handleReceptionDateChange(receptionDate) {
      this.$emit('update:receptionDatetime', receptionDate)
    },
    handleInitialLocationChange(location) {
      this.$emit('update:initialLocation', location)
    },
    handleFinalLocationChange(location) {
      this.$emit('update:finalLocation', location)
    },
    handleInitialKmChange(km) {
      this.$emit('update:initialKm', km)
    },
    handleFinalKmChange(km) {
      this.$emit('update:finalKm', km)
    },
    handleEquipmentChange(equipment, value) {
      this.$emit(`update:${equipment}`, value)
    },
    handleCustomerSignature() {
      this.$emit('show-customer-signature')
    },
    handleCustomerSignatureReturn() {
      this.$emit('show-customer-signature-return')
    },
    convertDatetimeToTimestamp(datetime) {
      if (!datetime) return null
      // If it's already a number (timestamp), return as is
      if (typeof datetime === 'number') return datetime
      // If it's a string, convert to timestamp
      if (typeof datetime === 'string') {
        const momentDate = moment(datetime)
        return momentDate.isValid() ? momentDate.unix() : null
      }
      return null
    },
    convertEndDateToTimestamp(endDate) {
      if (!endDate) return null
      // If it's already a number (timestamp), return as is
      if (typeof endDate === 'number') return endDate
      // If it's a string, convert to timestamp
      if (typeof endDate === 'string') {
        const momentDate = moment(endDate)
        return momentDate.isValid() ? momentDate.unix() : null
      }
      return null
    },
    convertReceptionDatetimeToTimestamp(receptionDatetime) {
      if (!receptionDatetime) return null
      // If it's already a number (timestamp), return as is
      if (typeof receptionDatetime === 'number') return receptionDatetime
      // If it's a string, convert to timestamp
      if (typeof receptionDatetime === 'string') {
        const momentDate = moment(receptionDatetime)
        return momentDate.isValid() ? momentDate.unix() : null
      }
      return null
    },
    handleCompleteMovement() {
      // Reset alert
      this.showIncompleteMovementsAlert = false
      
      // Validar estado de matrícula (obligatorio)
      if (!this.driverType || this.driverType === '') {
        this.$toast.error('Debe seleccionar el estado de matrícula para completar el movimiento')
        return
      }
      
      // Validar que si es transporte con grúa, debe tener una grúa seleccionada
      if (this.transportMethod === 'GRUA' && (this.lastMovement?.status === 'reported' || this.lastMovement?.status === 'processing') && !this.selectedGrua) {
        this.$toast.error('Debe seleccionar una empresa de grúa para completar el movimiento')
        return
      }
      
      // Validar conductor obligatorio para cesiones con transporte CLIENTE
      if (this.isDriverRequiredForAssignment && this.isDriverMissing) {
        this.$toast.error('El conductor es obligatorio para cesiones con transporte de cliente')
        return
      }
      
      // Validar que si es transporte con conductor (pero no CLIENTE en cesiones, ya validado arriba), debe tener un conductor seleccionado
      if (((this.transportMethod === 'YO MISMO' && this.lastMovement && this.lastMovement.status === 'reported')) && 
          !this.selectedDriver) {
        this.$toast.error('Debe seleccionar un conductor para completar el movimiento')
        return
      }
      
      // Validaciones específicas para estado 'reported' (entrega)
      if (this.lastMovement && this.lastMovement.status === 'reported') {
        // Validar kilómetros iniciales
        if (!this.initialKm || this.initialKm === '') {
          this.$toast.error('Debe ingresar los kilómetros iniciales para completar la entrega')
          return
        }
        
        // Validar firma de entrega obligatoria SOLO para cesiones (assignment)
        if (this.lastMovement.type === 'assignment' && this.shouldShowCustomerSignature && !this.customerSignatureDataURL) {
          this.$toast.error('La firma del conductor es obligatoria para completar la entrega de una cesión')
          return
        }
        
        // Validar firma del segundo conductor si está activo
        if (this.lastMovement.type === 'assignment' && this.shouldShowDriver2Signature && !this.driver2SignatureDataURL) {
          this.$toast.error('La firma del segundo conductor es obligatoria para completar la entrega')
          return
        }
      }
      
      // Validaciones específicas para estado 'processing' (recepción)
      if (this.lastMovement && this.lastMovement.status === 'processing') {
        // Validar kilómetros finales
        if (!this.finalKm || this.finalKm === '') {
          this.$toast.error('Debe ingresar los kilómetros finales para completar la recepción')
          return
        }
        
        // Validar que los kilómetros finales sean mayores que los iniciales
        if (this.initialKm && this.finalKm && parseFloat(this.finalKm) < parseFloat(this.initialKm)) {
          this.$toast.error('Los kilómetros finales deben ser mayores que los kilómetros iniciales')
          return
        }
        
        // Validar firma de recepción obligatoria SOLO para cesiones (assignment)
        if (this.lastMovement.type === 'assignment' && this.shouldShowCustomerSignatureReturn && !this.customerSignatureReturnDataURL) {
          this.$toast.error('La firma del conductor es obligatoria para completar la recepción de una cesión')
          return
        }
        
        // Validar firma de recepción del segundo conductor si está activo
        if (this.lastMovement.type === 'assignment' && this.shouldShowDriver2SignatureReturn && !this.driver2SignatureReturnDataURL) {
          this.$toast.error('La firma del segundo conductor es obligatoria para completar la recepción')
          return
        }
      }
      
      // Check for date conflicts with incomplete movements
      if (this.lastMovement && this.lastMovement.status === 'processing' && 
          this.status && this.status.movements && this.receptionDatetime) {
        const incompleteMovements = this.status.movements.filter(m => m.status !== 'completed' && m.id !== this.lastMovement.id)
        const hasDateConflict = incompleteMovements.some(m => {
          const movementStart = moment.unix(m.datetime).tz('Europe/Madrid')
          const receptionTime = moment.unix(this.receptionDatetime).tz('Europe/Madrid')
          return movementStart.isBefore(receptionTime)
        })
        if (hasDateConflict) {
          this.showIncompleteMovementsAlert = true
          return
        }
      }
      
      this.$emit('complete-movement')
    },
    
    // Customer signature methods for delivery (entrega)
    initCustomerSignaturePad() {
      this.$emit('update:showCustomerSignature', true)
      this.$nextTick(() => {
        const canvas = this.$refs.customerSignatureCanvas
        if (canvas) {
          canvas.width = 400
          canvas.height = 200
          this.customerSignaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    
    clearCustomerSignature() {
      if (this.customerSignaturePad) {
        this.customerSignaturePad.clear()
      }
    },
    
    saveCustomerSignature() {
      if (this.customerSignaturePad && !this.customerSignaturePad.isEmpty()) {
        const dataURL = this.customerSignaturePad.toDataURL()
        this.$emit('update:customerSignatureDataURL', dataURL)
        this.$emit('update:showCustomerSignature', false)
      } else {
        this.$toast.warning('Por favor, firme antes de guardar')
      }
    },
    
    editCustomerSignature() {
      this.$emit('update:showCustomerSignature', true)
      this.$nextTick(() => {
        const canvas = this.$refs.customerSignatureCanvas
        if (canvas && this.customerSignatureDataURL) {
          canvas.width = 400
          canvas.height = 200
          this.customerSignaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
          // Load existing signature
          const img = new Image()
          img.onload = () => {
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            this.customerSignaturePad._fromData(this.customerSignaturePad._getSignatureData())
          }
          img.src = this.customerSignatureDataURL
        }
      })
    },
    
    // Customer signature methods for return (devolución)  
    initCustomerSignatureReturnPad() {
      this.$emit('update:showCustomerSignatureReturn', true)
      this.$nextTick(() => {
        const canvas = this.$refs.customerSignatureReturnCanvas
        if (canvas) {
          canvas.width = 400
          canvas.height = 200
          this.customerSignatureReturnPad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    
    clearCustomerSignatureReturn() {
      if (this.customerSignatureReturnPad) {
        this.customerSignatureReturnPad.clear()
      }
    },
    
    saveCustomerSignatureReturn() {
      if (this.customerSignatureReturnPad && !this.customerSignatureReturnPad.isEmpty()) {
        const dataURL = this.customerSignatureReturnPad.toDataURL()
        this.$emit('update:customerSignatureReturnDataURL', dataURL)
        this.$emit('update:showCustomerSignatureReturn', false)
      } else {
        this.$toast.warning('Por favor, firme antes de guardar')
      }
    },
    
    editCustomerSignatureReturn() {
      this.$emit('update:showCustomerSignatureReturn', true)
      this.$nextTick(() => {
        const canvas = this.$refs.customerSignatureReturnCanvas
        if (canvas && this.customerSignatureReturnDataURL) {
          canvas.width = 400
          canvas.height = 200
          this.customerSignatureReturnPad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
          // Load existing signature
          const img = new Image()
          img.onload = () => {
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            this.customerSignatureReturnPad._fromData(this.customerSignatureReturnPad._getSignatureData())
          }
          img.src = this.customerSignatureReturnDataURL
        }
      })
    },
    
    // Driver 2 signature methods for delivery (entrega)
    initDriver2SignaturePad() {
      this.$emit('update:showDriver2Signature', true)
      this.$nextTick(() => {
        const canvas = this.$refs.driver2SignatureCanvas
        if (canvas) {
          canvas.width = 400
          canvas.height = 200
          this.driver2SignaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    
    clearDriver2Signature() {
      if (this.driver2SignaturePad) {
        this.driver2SignaturePad.clear()
      }
    },
    
    saveDriver2Signature() {
      if (this.driver2SignaturePad && !this.driver2SignaturePad.isEmpty()) {
        const dataURL = this.driver2SignaturePad.toDataURL()
        this.$emit('update:driver2SignatureDataURL', dataURL)
        this.$emit('update:showDriver2Signature', false)
      } else {
        this.$toast.warning('Por favor, firme antes de guardar')
      }
    },
    
    editDriver2Signature() {
      this.$emit('update:showDriver2Signature', true)
      this.$nextTick(() => {
        const canvas = this.$refs.driver2SignatureCanvas
        if (canvas && this.driver2SignatureDataURL) {
          canvas.width = 400
          canvas.height = 200
          this.driver2SignaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
          // Load existing signature
          const img = new Image()
          img.onload = () => {
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            this.driver2SignaturePad._fromData(this.driver2SignaturePad._getSignatureData())
          }
          img.src = this.driver2SignatureDataURL
        }
      })
    },
    
    // Driver 2 signature methods for return (devolución)
    initDriver2SignatureReturnPad() {
      this.$emit('update:showDriver2SignatureReturn', true)
      this.$nextTick(() => {
        const canvas = this.$refs.driver2SignatureReturnCanvas
        if (canvas) {
          canvas.width = 400
          canvas.height = 200
          this.driver2SignatureReturnPad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
        }
      })
    },
    
    clearDriver2SignatureReturn() {
      if (this.driver2SignatureReturnPad) {
        this.driver2SignatureReturnPad.clear()
      }
    },
    
    saveDriver2SignatureReturn() {
      if (this.driver2SignatureReturnPad && !this.driver2SignatureReturnPad.isEmpty()) {
        const dataURL = this.driver2SignatureReturnPad.toDataURL()
        this.$emit('update:driver2SignatureReturnDataURL', dataURL)
        this.$emit('update:showDriver2SignatureReturn', false)
      } else {
        this.$toast.warning('Por favor, firme antes de guardar')
      }
    },
    
    editDriver2SignatureReturn() {
      this.$emit('update:showDriver2SignatureReturn', true)
      this.$nextTick(() => {
        const canvas = this.$refs.driver2SignatureReturnCanvas
        if (canvas && this.driver2SignatureReturnDataURL) {
          canvas.width = 400
          canvas.height = 200
          this.driver2SignatureReturnPad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5
          })
          // Load existing signature
          const img = new Image()
          img.onload = () => {
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            this.driver2SignatureReturnPad._fromData(this.driver2SignatureReturnPad._getSignatureData())
          }
          img.src = this.driver2SignatureReturnDataURL
        }
      })
    },
    
    handleSelectedEquipmentsUpdate(selectedEquipments) {
      // Emitir el objeto completo de equipamientos seleccionados al componente padre
      this.$emit('update:selectedEquipments', selectedEquipments)
    },
    getFileIcon(filename) {
      if (!filename) return 'mdi-file-outline'
      const extension = filename.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        txt: 'mdi-file-document-outline',
        zip: 'mdi-folder-zip',
        rar: 'mdi-folder-zip'
      }
      return iconMap[extension] || 'mdi-file-outline'
    },
    getFileIconColor(filename) {
      if (!filename) return 'grey'
      const extension = filename.split('.').pop().toLowerCase()
      const colorMap = {
        pdf: 'red',
        doc: 'blue',
        docx: 'blue',
        xls: 'green',
        xlsx: 'green',
        txt: 'grey',
        zip: 'orange',
        rar: 'orange'
      }
      return colorMap[extension] || 'grey'
    },
    downloadDoc(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'documento'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    removeDoc(index) {
      const doc = this.formattedSavedAdditionalDocs[index]
      const docId = doc && doc.id !== undefined ? doc.id : index
      const updatedDocsToRemove = [...this.additionalDocsToRemove, docId]
      this.$emit('update:additionalDocsToRemove', updatedDocsToRemove)
      this.$emit('additional-doc-removed', { type: 'saved', docId })
    },
    removeNewDoc(index) {
      const updatedDocs = [...this.newAdditionalDocs]
      updatedDocs.splice(index, 1)
      this.$emit('update:newAdditionalDocs', updatedDocs)
      this.$emit('additional-doc-removed', { type: 'new', index })
    },
    handleDocUpload(files) {
      if (!files || files.length === 0) return

      const existingFiles = this.newAdditionalDocs || []
      const newFiles = Array.from(files)

      const totalSlots = 4
      const savedCount = this.formattedSavedAdditionalDocs ? this.formattedSavedAdditionalDocs.length - this.additionalDocsToRemove.length : 0
      const usedSlots = savedCount + existingFiles.length
      const availableSlots = totalSlots - usedSlots

      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} documentos`)
        this.tempDocs = []
        return
      }

      let filesToProcess = newFiles
      let warningMessage = ''

      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} documento(s). Se rechazaron ${rejected} documento(s) por exceder el límite de ${totalSlots}.`
      }

      const updatedFiles = [...existingFiles, ...filesToProcess]
      this.$emit('update:newAdditionalDocs', updatedFiles)

      const totalDocs = savedCount + updatedFiles.length
      if (warningMessage) {
        this.$toast.warning(warningMessage)
      } else {
        this.$toast.success(`${filesToProcess.length} documento(s) agregado(s). Total: ${totalDocs}/${totalSlots}`)
      }

      this.$nextTick(() => {
        this.tempDocs = []
      })
    },
    removeDamageDoc(index) {
      const doc = this.formattedSavedDamageDocs[index]
      const docId = doc && doc.id !== undefined ? doc.id : index
      const updatedDocsToRemove = [...this.damageDocsToRemove, docId]
      this.$emit('update:damageDocsToRemove', updatedDocsToRemove)
      this.$emit('damage-doc-removed', { type: 'saved', docId })
    },
    removeNewDamageDoc(index) {
      const updatedDocs = [...this.newDamageDocs]
      updatedDocs.splice(index, 1)
      this.$emit('update:newDamageDocs', updatedDocs)
      this.$emit('damage-doc-removed', { type: 'new', index })
    },
    handleDamageDocUpload(files) {
      if (!files || files.length === 0) return

      const existingFiles = this.newDamageDocs || []
      const newFiles = Array.from(files)

      const totalSlots = 4
      const savedCount = this.formattedSavedDamageDocs ? this.formattedSavedDamageDocs.length - this.damageDocsToRemove.length : 0
      const usedSlots = savedCount + existingFiles.length
      const availableSlots = totalSlots - usedSlots

      if (availableSlots === 0) {
        this.$toast.warning(`Ya has alcanzado el límite máximo de ${totalSlots} documentos de daños`)
        this.tempDamageDocs = []
        return
      }

      let filesToProcess = newFiles
      let warningMessage = ''

      if (newFiles.length > availableSlots) {
        filesToProcess = newFiles.slice(0, availableSlots)
        const rejected = newFiles.length - availableSlots
        warningMessage = `Solo se agregaron ${availableSlots} documento(s). Se rechazaron ${rejected} documento(s) por exceder el límite de ${totalSlots}.`
      }

      const updatedFiles = [...existingFiles, ...filesToProcess]
      this.$emit('update:newDamageDocs', updatedFiles)

      const totalDocs = savedCount + updatedFiles.length
      if (warningMessage) {
        this.$toast.warning(warningMessage)
      } else {
        this.$toast.success(`${filesToProcess.length} documento(s) de daños agregado(s). Total: ${totalDocs}/${totalSlots}`)
      }

      this.$nextTick(() => {
        this.tempDamageDocs = []
      })
    },
    async fetchImageAsDataURL(url) {
      try {
        const response = await fetch(url)
        const blob = await response.blob()
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error('Error fetching image as data URL:', error)
        return null
      }
    }
  }
}
</script>

<style scoped>
.movement-processing-view {
  width: 100%;
}

.movement-details-card {
  border-left: 4px solid var(--v-primary-base);
}

.final-km-card {
  border-left: 4px solid var(--v-success-base);
}

.observations-card {
  border-left: 4px solid var(--v-info-base);
}

.signatures-card {
  border-left: 4px solid var(--v-purple-base);
}

.signature-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background-color: #fafafa;
}

.signature-preview {
  text-align: center;
}

.signature-image {
  max-width: 100%;
  max-height: 100px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
}

.detail-item {
  margin-bottom: 12px;
}

.detail-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
  margin-bottom: 2px;
}

.detail-value {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
}

.v-card-title {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Vehicle Condition Styles */
.vehicle-condition-group .v-radio {
  display: none;
}

.vehicle-condition-group .v-card {
  cursor: pointer;
  transition: all 0.3s ease;
}

.vehicle-condition-group .v-card:hover {
  transform: translateY(-2px);
}

.vehicle-condition-group .disabled-card {
  cursor: not-allowed;
  opacity: 0.6;
}

.vehicle-condition-group .disabled-card:hover {
  transform: none !important;
}

.vehicle-condition-radio {
  display: flex !important;
  justify-content: center !important;
  margin: 8px 0 0 0 !important;
}

/* Additional styles for processing view */
.processing-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: white;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.info-section {
  margin-bottom: 24px;
}

.info-card {
  border-left: 4px solid #1976d2;
  margin-bottom: 16px;
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.equipment-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
}

.equipment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.equipment-card.selected {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.05);
}

.signature-section {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin-top: 24px;
}

.completion-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  margin-top: 24px;
}

/* Additional Images Styles */
.additional-images-section {
  margin-top: 16px;
}

.image-preview-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.image-preview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-preview-card .v-card__actions {
  min-height: auto;
  padding: 4px 8px;
}

.disabled-item {
  opacity: 0.5;
}

.new-doc-item {
  background-color: rgba(76, 175, 80, 0.05);
  border-radius: 4px;
  margin-bottom: 4px;
}

/* Custom info cards with borders */
.info-card-blue {
  background-color: #E3F2FD !important;
  border: 2px solid #1976D2 !important;
  border-radius: 8px !important;
}

.info-card-green {
  background-color: #E8F5E9 !important;
  border: 2px solid #4CAF50 !important;
  border-radius: 8px !important;
}
</style>
